import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  renderRedPenAnnotationsToCanvas,
  hitTestMarking,
  getMarkingBounds,
} from "./redPenRenderer";
import { swalToast, swalConfirm } from "../../../../utils/swalAlert";

// Helper to dynamically ensure PDF.js is loaded
const ensurePdfJs = async () => {
  if (window.pdfjsLib) return window.pdfjsLib;

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.async = true;
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        resolve(window.pdfjsLib);
      } else {
        reject(new Error("pdfjsLib not defined after script load"));
      }
    };
    script.onerror = () => reject(new Error("Failed to load PDF.js from CDN"));
    document.head.appendChild(script);
  });
};

// Helper to sanitize and deduplicate scoreData (removes corrupted duplicate annotations/steps)
const sanitizeScoreData = (data) => {
  if (!data?.result || !Array.isArray(data.result)) return data;
  const cleanedResult = data.result.map((q) => {
    // 1. Deduplicate custom annotations
    const seenAnnIds = new Set();
    const seenCoords = new Set();
    const cleanAnns = [];

    (q.annotations || []).forEach((a) => {
      if (!a) return;
      const aId = String(a._id || "");
      // Filter out corrupt alias IDs from previous bug
      if (a.isCustom && /^(?:q)?\d+-(?:a)?\d+$/.test(aId)) return;
      if (aId && seenAnnIds.has(aId)) return;

      // Also deduplicate identical custom comments generated at the exact same location
      if (a.isCustom && (a.annotationType === "custom_note" || a.commentCustomX != null)) {
        const x = Number(a.commentCustomX ?? a.customX ?? 0).toFixed(3);
        const y = Number(a.commentCustomY ?? a.customY ?? 0).toFixed(3);
        const key = `${a.pageNumber || 1}_${x}_${y}_${a.comment || ""}`;
        if (seenCoords.has(key)) return;
        seenCoords.add(key);
      }

      if (aId) seenAnnIds.add(aId);
      cleanAnns.push(a);
    });

    // 2. Deduplicate custom steps
    const seenStepIds = new Set();
    const seenStepCoords = new Set();
    const cleanSteps = [];

    (q.stepEvaluations || []).forEach((s) => {
      if (!s) return;
      const sId = String(s._id || "");
      if (s.isCustom && /^(?:q)?\d+-(?:s)?\d+$/.test(sId)) return;
      if (sId && seenStepIds.has(sId)) return;

      if (s.isCustom && s.customX != null && s.customY != null) {
        const x = Number(s.customX).toFixed(3);
        const y = Number(s.customY).toFixed(3);
        const key = `${s.pageNumber || 1}_${x}_${y}_${s.status || ""}`;
        if (seenStepCoords.has(key)) return;
        seenStepCoords.add(key);
      }

      if (sId) seenStepIds.add(sId);
      cleanSteps.push(s);
    });

    return {
      ...q,
      annotations: cleanAnns,
      stepEvaluations: cleanSteps,
    };
  });

  return {
    ...data,
    result: cleanedResult,
  };
};

// Helper to expand aliases across scoreData
const expandPositionAliases = (posMap, sData) => {
  const result = { ...(posMap || {}) };

  // 1. First expand any known key patterns (skip custom markings)
  Object.keys(posMap || {}).forEach((k) => {
    const val = posMap[k];
    if (!val || typeof val !== "object") return;
    if (val.isCustom || k.includes("-custom-") || k.startsWith("custom-")) {
      return;
    }

    // Matches step-mark-1-0 or step-mark-q1-s0 or step-mark-1-s0
    let m = k.match(/^step-mark-(?:q)?([a-zA-Z0-9._-]+)-(?:s)?(\d+)$/);
    if (m) {
      const qNum = m[1];
      const sIdx = m[2];
      result[`step-mark-${qNum}-${sIdx}`] = val;
      result[`step-mark-q${qNum}-s${sIdx}`] = val;
    }

    // Matches ann-loop-1-0 or ann-loop-q1-a0 or ann-loop-1-a0
    m = k.match(/^ann-loop-(?:q)?([a-zA-Z0-9._-]+)-(?:a)?(\d+)$/);
    if (m) {
      const qNum = m[1];
      const aIdx = m[2];
      result[`ann-loop-${qNum}-${aIdx}`] = val;
      result[`ann-loop-q${qNum}-a${aIdx}`] = val;
    }

    // Matches ann-comment-1-0 or ann-comment-q1-a0 or ann-comment-1-a0
    m = k.match(/^ann-comment-(?:q)?([a-zA-Z0-9._-]+)-(?:a)?(\d+)$/);
    if (m) {
      const qNum = m[1];
      const aIdx = m[2];
      result[`ann-comment-${qNum}-${aIdx}`] = val;
      result[`ann-comment-q${qNum}-a${aIdx}`] = val;
    }

    // Matches ann-mark-1-0 or ann-mark-q1-a0 or ann-mark-1-a0
    m = k.match(/^ann-mark-(?:q)?([a-zA-Z0-9._-]+)-(?:a)?(\d+)$/);
    if (m) {
      const qNum = m[1];
      const aIdx = m[2];
      result[`ann-mark-${qNum}-${aIdx}`] = val;
      result[`ann-mark-q${qNum}-a${aIdx}`] = val;
      if (!result[`ann-loop-${qNum}-${aIdx}`]) result[`ann-loop-${qNum}-${aIdx}`] = val;
    }

    // Matches q-score-1 or q-score-q1 or q-score-1.5
    m = k.match(/^q-score-(?:q)?([a-zA-Z0-9._-]+)$/);
    if (m) {
      const qNum = m[1];
      result[`q-score-${qNum}`] = val;
      result[`q-score-q${qNum}`] = val;
    }
  });

  // 2. Also map by scoreData question list (matching questionNumber and index)
  if (sData?.result && Array.isArray(sData.result)) {
    sData.result.forEach((q, qIdx) => {
      const qNum = q.questionNumber || `${qIdx + 1}`;
      const qKey = String(qNum).trim();
      const qIdx1 = `${qIdx + 1}`;
      const qId = q._id ? String(q._id) : null;

      // Question score
      const qScoreVal =
        result[`q-score-${qKey}`] ||
        result[`q-score-q${qKey}`] ||
        (qId && result[`q-score-${qId}`]) ||
        (qId && result[qId]) ||
        result[`q-score-${qIdx1}`] ||
        result[`q-score-q${qIdx1}`];
      if (qScoreVal) {
        result[`q-score-${qKey}`] = qScoreVal;
        result[`q-score-q${qKey}`] = qScoreVal;
        result[`q-score-${qIdx1}`] = qScoreVal;
        if (qId) {
          result[`q-score-${qId}`] = qScoreVal;
        }
      }

      // Steps (skip custom steps)
      if (q.stepEvaluations && Array.isArray(q.stepEvaluations)) {
        q.stepEvaluations.forEach((step, sIdx) => {
          if (step.isCustom || step._id?.includes("custom-")) return;
          const stepVal =
            (step._id && result[`step-mark-${step._id}`]) ||
            result[`step-mark-${qKey}-${sIdx}`] ||
            result[`step-mark-q${qKey}-s${sIdx}`] ||
            result[`step-mark-${qIdx1}-${sIdx}`] ||
            result[`step-mark-q${qIdx1}-s${sIdx}`] ||
            result[`step-mark-p${step.pageNumber || 1}-s${sIdx}`];

          if (stepVal) {
            if (step._id) result[`step-mark-${step._id}`] = stepVal;
            result[`step-mark-${qKey}-${sIdx}`] = stepVal;
            result[`step-mark-q${qKey}-s${sIdx}`] = stepVal;
            result[`step-mark-${qIdx1}-${sIdx}`] = stepVal;
          }
        });
      }

      // Annotations (skip custom annotations)
      if (q.annotations && Array.isArray(q.annotations)) {
        q.annotations.forEach((ann, aIdx) => {
          if (ann.isCustom || ann._id?.includes("custom-")) return;
          const annVal =
            (ann._id && result[`ann-loop-${ann._id}`]) ||
            result[`ann-loop-${qKey}-${aIdx}`] ||
            result[`ann-loop-q${qKey}-a${aIdx}`] ||
            result[`ann-loop-${qIdx1}-${aIdx}`] ||
            result[`ann-loop-q${qIdx1}-a${aIdx}`] ||
            (ann._id && result[`ann-mark-${ann._id}`]) ||
            result[`ann-mark-${qKey}-${aIdx}`] ||
            result[`ann-mark-q${qKey}-a${aIdx}`];

          if (annVal) {
            if (ann._id) {
              result[`ann-loop-${ann._id}`] = annVal;
              result[`ann-mark-${ann._id}`] = annVal;
            }
            result[`ann-loop-${qKey}-${aIdx}`] = annVal;
            result[`ann-loop-q${qKey}-a${aIdx}`] = annVal;
            result[`ann-loop-${qIdx1}-${aIdx}`] = annVal;
            result[`ann-mark-${qKey}-${aIdx}`] = annVal;
            result[`ann-mark-q${qKey}-a${aIdx}`] = annVal;
          }

          const commentVal =
            (ann._id && result[`ann-comment-${ann._id}`]) ||
            result[`ann-comment-${qKey}-${aIdx}`] ||
            result[`ann-comment-q${qKey}-a${aIdx}`] ||
            result[`ann-comment-${qIdx1}-${aIdx}`] ||
            result[`ann-comment-q${qIdx1}-a${aIdx}`];

          if (commentVal) {
            if (ann._id) result[`ann-comment-${ann._id}`] = commentVal;
            result[`ann-comment-${qKey}-${aIdx}`] = commentVal;
            result[`ann-comment-q${qKey}-a${aIdx}`] = commentVal;
            result[`ann-comment-${qIdx1}-${aIdx}`] = commentVal;
          }
        });
      }
    });
  }

  // Remove any stale positional aliases that point to custom marks
  Object.keys(result).forEach((k) => {
    if (/^(?:ann-comment|ann-loop|ann-mark|step-mark)-(?:q)?\d+-(?:[as])?\d+$/.test(k)) {
      if (result[k]?.isCustom || result[k]?.annotationId?.includes("custom-") || result[k]?.stepId?.includes("custom-")) {
        delete result[k];
      }
    }
  });

  return result;
};

// Helper to extract positions from scoreData
const extractPositionsFromScoreData = (data) => {
  let pos = expandPositionAliases(data?.annotationPositions || {}, data);
  if (data?.result && Array.isArray(data.result)) {
    data.result.forEach((q, qIdx) => {
      const qNum = q.questionNumber || `${qIdx + 1}`;
      const qKey = String(qNum).trim();
      if (q.scorePosition?.customX != null && q.scorePosition?.customY != null) {
        const val = {
          xRatio: q.scorePosition.customX,
          yRatio: q.scorePosition.customY,
          pageNum: q.scorePosition.pageNumber || 1,
        };
        pos[`q-score-${qKey}`] = pos[`q-score-${qKey}`] || val;
        pos[`q-score-q${qKey}`] = pos[`q-score-q${qKey}`] || val;
      }
      if (q.stepEvaluations && Array.isArray(q.stepEvaluations)) {
        q.stepEvaluations.forEach((step, sIdx) => {
          if (step.customX != null && step.customY != null) {
            const isTick = step.status === "correct";
            const val = {
              xRatio: step.customX,
              yRatio: step.customY,
              pageNum: step.pageNumber || 1,
              scale: step.scale || 1.0,
              deleted: Boolean(step.deleted),
              isCustom: Boolean(step.isCustom),
              status: step.status || "correct",
              type: isTick ? "tick" : "cross",
              markType: "step-mark",
              label: `${isTick ? "Checkmark" : "Crossmark"} (Q${qKey})`,
              questionIndex: qIdx,
              questionId: q._id,
              questionNumber: qNum,
              stepIndex: sIdx,
              stepId: step._id,
            };
            if (step._id) pos[`step-mark-${step._id}`] = pos[`step-mark-${step._id}`] || val;
            if (!step.isCustom && !step._id?.includes("custom-")) {
              pos[`step-mark-${qKey}-${sIdx}`] = pos[`step-mark-${qKey}-${sIdx}`] || val;
              pos[`step-mark-q${qKey}-s${sIdx}`] = pos[`step-mark-q${qKey}-s${sIdx}`] || val;
            }
          }
        });
      }
      if (q.annotations && Array.isArray(q.annotations)) {
        q.annotations.forEach((ann, aIdx) => {
          if (ann.customX != null && ann.customY != null) {
            const isLine = ann.underline === true && ann.circleLine === false;
            const val = {
              xRatio: ann.customX,
              yRatio: ann.customY,
              pageNum: ann.pageNumber || 1,
              scale: ann.scale || 1.0,
              radiusRatio: ann.radiusRatio,
              heightRatio: ann.heightRatio,
              strokeWidth: ann.strokeWidth || 2.0,
              deleted: Boolean(ann.deleted),
              isCustom: Boolean(ann.isCustom),
              type: isLine ? "line" : "circle",
              markType: "ann-loop",
              label: `${isLine ? "Horizontal Line" : "Circle Loop"} (Q${qKey})`,
              questionIndex: qIdx,
              questionId: q._id,
              questionNumber: qNum,
              annotationIndex: aIdx,
              annotationId: ann._id,
            };
            if (ann._id) {
              pos[`ann-loop-${ann._id}`] = pos[`ann-loop-${ann._id}`] || val;
              pos[`ann-mark-${ann._id}`] = pos[`ann-mark-${ann._id}`] || val;
            }
            if (!ann.isCustom && !ann._id?.includes("custom-")) {
              pos[`ann-loop-${qKey}-${aIdx}`] = pos[`ann-loop-${qKey}-${aIdx}`] || val;
              pos[`ann-loop-q${qKey}-a${aIdx}`] = pos[`ann-loop-q${qKey}-a${aIdx}`] || val;
              pos[`ann-mark-${qKey}-${aIdx}`] = pos[`ann-mark-${qKey}-${aIdx}`] || val;
              pos[`ann-mark-q${qKey}-a${aIdx}`] = pos[`ann-mark-q${qKey}-a${aIdx}`] || val;
            }
          }

          if (ann.commentCustomX != null && ann.commentCustomY != null) {
            const cVal = {
              xRatio: ann.commentCustomX,
              yRatio: ann.commentCustomY,
              pageNum: ann.pageNumber || 1,
              text: ann.comment || ann.suggestion,
              scale: ann.commentScale || ann.scale || 1.0,
              fontSize: ann.fontSize || 11,
              fontFamily: ann.fontFamily || "sans",
              deleted: Boolean(ann.deletedComment || ann.deleted),
              isCustom: Boolean(ann.isCustom),
              type: "comment",
              markType: "comment",
              label: `Comment (Q${qKey})`,
              questionIndex: qIdx,
              questionId: q._id,
              questionNumber: qNum,
              annotationIndex: aIdx,
              annotationId: ann._id,
            };
            if (ann._id) pos[`ann-comment-${ann._id}`] = pos[`ann-comment-${ann._id}`] || cVal;
            if (!ann.isCustom && !ann._id?.includes("custom-")) {
              pos[`ann-comment-${qKey}-${aIdx}`] = pos[`ann-comment-${qKey}-${aIdx}`] || cVal;
              pos[`ann-comment-q${qKey}-a${aIdx}`] = pos[`ann-comment-q${qKey}-a${aIdx}`] || cVal;
            }
          }
        });
      }
    });
  }

  // Extract Top Score Stamp position
  if (data?.topScorePosition && (data.topScorePosition.customX != null || data.topScorePosition.customY != null || data.topScorePosition.scale != null)) {
    pos["top-score-stamp"] = {
      xRatio: data.topScorePosition.customX,
      yRatio: data.topScorePosition.customY,
      scale: data.topScorePosition.scale || 1.0,
      pageNum: data.topScorePosition.pageNumber || 1,
      deleted: Boolean(data.topScorePosition.deleted),
      type: "top-score",
      markType: "top-score",
      label: "Total Score Stamp",
    };
  }

  return pos;
};

// Helper to serialize annotation positions into a stable comparable string for dirty tracking
const serializePositions = (posMap) => {
  if (!posMap || typeof posMap !== "object") return "";
  const sortedKeys = Object.keys(posMap).sort();
  const cleanObj = {};
  for (const k of sortedKeys) {
    const val = posMap[k];
    if (!val || typeof val !== "object") continue;
    cleanObj[k] = {
      xRatio: val.xRatio != null ? Number(Number(val.xRatio).toFixed(4)) : undefined,
      yRatio: val.yRatio != null ? Number(Number(val.yRatio).toFixed(4)) : undefined,
      x2Ratio: val.x2Ratio != null ? Number(Number(val.x2Ratio).toFixed(4)) : undefined,
      y2Ratio: val.y2Ratio != null ? Number(Number(val.y2Ratio).toFixed(4)) : undefined,
      radiusRatio: val.radiusRatio != null ? Number(Number(val.radiusRatio).toFixed(4)) : undefined,
      scale: val.scale != null ? Number(Number(val.scale).toFixed(3)) : undefined,
      pageNum: val.pageNum != null ? Number(val.pageNum) : undefined,
      color: val.color,
      text: val.text,
      fontSize: val.fontSize,
      fontFamily: val.fontFamily,
      strokeWidth: val.strokeWidth,
      deleted: Boolean(val.deleted),
      score: val.score != null ? Number(val.score) : undefined,
    };
  }
  return JSON.stringify(cleanObj);
};

const serializeStudioState = (posMap, modifiedScores) => {
  const posStr = serializePositions(posMap);
  const scoreKeys = Object.keys(modifiedScores || {}).sort();
  const cleanScores = {};
  for (const sk of scoreKeys) {
    const item = modifiedScores[sk];
    if (item && item.obtainedScore !== undefined) {
      cleanScores[sk] = Number(item.obtainedScore);
    }
  }
  return `${posStr}::${JSON.stringify(cleanScores)}`;
};

/**
 * Floating Quick-Action Toolbar.
 * Floats directly above / near the selected marking on the canvas.
 * Allows quick one-click score increments (+/- 0.5), size scaling (+/- 10%),
 * text editing, mark toggling, or deletion without needing a sidebar.
 */
const StudioFloatingToolbar = ({
  selectedMark,
  canvas,
  pageNum,
  onUpdate,
  onDelete,
  onClose,
  questions = [],
  onReassignQuestion,
  onDuplicate,
}) => {
  if (!selectedMark || !canvas) return null;
  if (selectedMark.pageNum && pageNum && Number(selectedMark.pageNum) !== Number(pageNum)) return null;

  const bounds = getMarkingBounds(selectedMark, canvas);
  if (!bounds) return null;

  const canvasWidth = canvas.width || 1;
  const canvasHeight = canvas.height || 1;
  const clientWidth = canvas.clientWidth || canvasWidth;
  const clientHeight = canvas.clientHeight || canvasHeight;

  const domCx = (bounds.cx / canvasWidth) * clientWidth;
  const domTop = (bounds.top / canvasHeight) * clientHeight;
  const domBottom = (bounds.bottom / canvasHeight) * clientHeight;

  // Position above the mark, or below if mark is near top of page
  const toolbarTop = domTop > 54 ? domTop - 52 : domBottom + 12;
  const toolbarLeft = Math.max(10, Math.min(clientWidth - 310, domCx - 150));

  const markType = (selectedMark.type || selectedMark.markType || "").toLowerCase();
  const isTopScore = markType === "top-score" || selectedMark.id === "top-score-stamp";
  const isQuestionScore = markType === "question-score" || selectedMark.id?.startsWith("q-score");
  const isTick = selectedMark.type === "tick" || selectedMark.status === "correct";
  const isCross = selectedMark.type === "cross" || selectedMark.status === "incorrect";
  const isStepMark = isTick || isCross;
  const isComment = markType === "comment" || selectedMark.id?.includes("comment");

  const currentScale = selectedMark.scale || 1.0;
  const currentScore = selectedMark.score !== undefined ? Number(selectedMark.score) : 0;
  const maxScore = selectedMark.maxScore !== undefined ? Number(selectedMark.maxScore) : 1;

  const stepScale = (delta) => {
    const next = Math.max(0.4, Math.min(2.5, +(currentScale + delta).toFixed(2)));
    onUpdate({ scale: next });
  };

  const stepScore = (delta) => {
    const next = Math.max(0, Math.min(maxScore, +(currentScore + delta).toFixed(1)));
    onUpdate({ score: next });
  };

  return (
    <div
      className="studio-floating-toolbar"
      style={{
        top: `${toolbarTop}px`,
        left: `${toolbarLeft}px`,
      }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {isTopScore && (
        <>
          <div className="c-pill-badge top-score-badge">
            <i className="fa-solid fa-trophy" />
            <span>Overall Score</span>
          </div>
          <div className="c-pill-stepper">
            <span className="c-stepper-label">Size</span>
            <button type="button" className="c-step-btn" onClick={() => stepScale(-0.1)} title="Decrease size">
              <i className="fa-solid fa-minus" />
            </button>
            <span className="c-scale-val">{Math.round(currentScale * 100)}%</span>
            <button type="button" className="c-step-btn" onClick={() => stepScale(0.1)} title="Increase size">
              <i className="fa-solid fa-plus" />
            </button>
          </div>
          <span className="c-corner-hint">💡 Drag corners to resize</span>
        </>
      )}

      {isQuestionScore && (
        <>
          <div className="c-pill-badge q-score-badge">
            <span>Q{selectedMark.qNum || selectedMark.questionNumber}</span>
          </div>
          <div className="c-pill-stepper score-stepper">
            <span className="c-stepper-label">Marks</span>
            <button type="button" className="c-step-btn" onClick={() => stepScore(-0.5)} title="Minus 0.5">
              <i className="fa-solid fa-minus" />
            </button>
            <span className="c-score-val">{currentScore} / {maxScore}</span>
            <button type="button" className="c-step-btn" onClick={() => stepScore(0.5)} title="Plus 0.5">
              <i className="fa-solid fa-plus" />
            </button>
          </div>
          <div className="c-pill-stepper">
            <button type="button" className="c-step-btn" onClick={() => stepScale(-0.1)} title="Smaller">
              <i className="fa-solid fa-minus" />
            </button>
            <span className="c-scale-val">{Math.round(currentScale * 100)}%</span>
            <button type="button" className="c-step-btn" onClick={() => stepScale(0.1)} title="Bigger">
              <i className="fa-solid fa-plus" />
            </button>
          </div>
          {selectedMark.isCustom && (
            <button type="button" className="c-icon-action-btn del-btn" onClick={onDelete} title="Delete">
              <i className="fa-solid fa-trash-can" />
            </button>
          )}
        </>
      )}

      {isStepMark && (
        <>
          <button
            type="button"
            className="c-toggle-btn"
            onClick={() => onUpdate({ status: isTick ? "incorrect" : "correct", type: isTick ? "cross" : "tick" })}
            title="Switch mark"
          >
            <i className={`fa-solid ${isTick ? "fa-check" : "fa-xmark"}`} style={{ color: isTick ? "#22c55e" : "#ef4444" }} />
            <span>Switch to {isTick ? "✖ Cross" : "✔ Tick"}</span>
          </button>
          <div className="c-pill-stepper">
            <button type="button" className="c-step-btn" onClick={() => stepScale(-0.1)} title="Smaller">
              <i className="fa-solid fa-minus" />
            </button>
            <span className="c-scale-val">{Math.round(currentScale * 100)}%</span>
            <button type="button" className="c-step-btn" onClick={() => stepScale(0.1)} title="Bigger">
              <i className="fa-solid fa-plus" />
            </button>
          </div>
          <button type="button" className="c-icon-action-btn del-btn" onClick={onDelete} title="Delete">
            <i className="fa-solid fa-trash-can" />
          </button>
        </>
      )}

      {isComment && (
        <>
          <input
            type="text"
            className="c-inline-comment-input"
            value={selectedMark.text || ""}
            onChange={(e) => onUpdate({ text: e.target.value })}
            placeholder="Edit comment..."
          />
          <div className="c-pill-stepper">
            <button type="button" className="c-step-btn" onClick={() => stepScale(-0.1)} title="Smaller font">
              <i className="fa-solid fa-font" style={{ fontSize: "0.7rem" }} /> -
            </button>
            <button type="button" className="c-step-btn" onClick={() => stepScale(0.1)} title="Bigger font">
              <i className="fa-solid fa-font" style={{ fontSize: "0.85rem" }} /> +
            </button>
          </div>
          <button type="button" className="c-icon-action-btn del-btn" onClick={onDelete} title="Delete">
            <i className="fa-solid fa-trash-can" />
          </button>
        </>
      )}

      {!isTopScore && !isQuestionScore && !isStepMark && !isComment && (
        <>
          <div className="c-pill-badge">
            <span>{selectedMark.label || "Mark"}</span>
          </div>
          <div className="c-pill-stepper">
            <button type="button" className="c-step-btn" onClick={() => stepScale(-0.1)}>
              <i className="fa-solid fa-minus" />
            </button>
            <span className="c-scale-val">{Math.round(currentScale * 100)}%</span>
            <button type="button" className="c-step-btn" onClick={() => stepScale(0.1)}>
              <i className="fa-solid fa-plus" />
            </button>
          </div>
          <button type="button" className="c-icon-action-btn del-btn" onClick={onDelete} title="Delete">
            <i className="fa-solid fa-trash-can" />
          </button>
        </>
      )}

      {/* Question Selector & Duplicate for non-top-score marks */}
      {!isTopScore && questions && questions.length > 0 && selectedMark.questionIndex !== undefined && onReassignQuestion && (
        <select
          className="c-inline-q-select"
          value={selectedMark.questionIndex}
          onChange={(e) => onReassignQuestion(selectedMark.id, Number(e.target.value))}
          title="Attached Question"
        >
          {questions.map((q, idx) => (
            <option key={q._id || idx} value={idx}>
              Q{q.questionNumber || idx + 1}
            </option>
          ))}
        </select>
      )}

      {!isTopScore && onDuplicate && (
        <button
          type="button"
          className="c-icon-action-btn dup-btn"
          onClick={onDuplicate}
          title="Duplicate this mark"
        >
          <i className="fa-solid fa-copy" />
        </button>
      )}

      <button type="button" className="c-done-check-btn" onClick={onClose} title="Done / Deselect">
        <i className="fa-solid fa-check" />
      </button>
    </div>
  );
};

/**
 * Visual "+ Add Mark" Modal for non-technical teachers.
 * Displays large, clear visual tiles and dropdown to attach to a question.
 */
const StudioAddModal = ({
  isOpen,
  onClose,
  onAddMark,
  currentPage,
  questions = [],
}) => {
  if (!isOpen) return null;

  const markOptions = [
    {
      type: "tick",
      icon: "fa-solid fa-check",
      iconColor: "#16a34a",
      bgGradient: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
      title: "Correct (Tick)",
      desc: "Mark a correct step, line, or formula",
      tag: "Popular",
    },
    {
      type: "cross",
      icon: "fa-solid fa-xmark",
      iconColor: "#dc2626",
      bgGradient: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
      title: "Incorrect (Cross)",
      desc: "Flag a mistake or incorrect step",
      tag: "Popular",
    },
    {
      type: "comment",
      icon: "fa-solid fa-comment-dots",
      iconColor: "#2563eb",
      bgGradient: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
      title: "Teacher Remark",
      desc: "Add feedback notes or handwriting remark",
    },
    {
      type: "circle",
      icon: "fa-regular fa-circle",
      iconColor: "#dc2626",
      bgGradient: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
      title: "Circle Mistake",
      desc: "Red loop to highlight a word or number",
    },
    {
      type: "line",
      icon: "fa-solid fa-minus",
      iconColor: "#dc2626",
      bgGradient: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
      title: "Underline",
      desc: "Underline a sentence, theorem, or step",
    },
    {
      type: "question-score",
      icon: "fa-solid fa-award",
      iconColor: "#d97706",
      bgGradient: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
      title: "Question Score",
      desc: "Place score badge (e.g. 5/10) on paper",
    },
  ];

  const handleSelectMark = (opt) => {
    // Automatically find a question on the same page
    let targetQIdx = 0;
    if (questions && questions.length > 0) {
      const pageMatchIdx = questions.findIndex(
        (q) =>
          q.pageNumber === currentPage ||
          q.scorePosition?.pageNumber === currentPage ||
          (Array.isArray(q.annotations) && q.annotations.some((a) => a?.pageNumber === currentPage)) ||
          (Array.isArray(q.stepEvaluations) && q.stepEvaluations.some((s) => s?.pageNumber === currentPage))
      );
      targetQIdx = pageMatchIdx >= 0 ? pageMatchIdx : 0;
    }

    const defaultText = opt.type === "comment" ? "Teacher note" : "";
    onAddMark(opt.type, targetQIdx, defaultText);
    onClose();
  };

  return (
    <div className="studio-add-modal-overlay" onClick={onClose}>
      <div className="studio-add-modal" onClick={(e) => e.stopPropagation()}>
        <div className="studio-modal-header">
          <div className="studio-modal-header-titles">
            <span className="studio-modal-pill">Page {currentPage}</span>
            <h3>What would you like to add?</h3>
            <p>Click any mark below to place it directly on Page {currentPage}</p>
          </div>
          <button type="button" className="studio-modal-close-btn" onClick={onClose} title="Close">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="studio-tiles-grid">
          {markOptions.map((opt, idx) => (
            <button
              key={`${opt.type}-${idx}`}
              type="button"
              className="studio-tile-card"
              onClick={() => handleSelectMark(opt)}
            >
              <div className="studio-tile-icon-box" style={{ background: opt.bgGradient, color: opt.iconColor }}>
                <i className={opt.icon} />
              </div>
              <div className="studio-tile-info">
                <div className="studio-tile-title-row">
                  <span className="studio-tile-title">{opt.title}</span>
                  {opt.tag && <span className="studio-tile-tag">{opt.tag}</span>}
                </div>
                <span className="studio-tile-desc">{opt.desc}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Accurately determines if pointer coordinate is hovering/clicking a resize handle of a selected mark.
 * Crucially protects the inner body / middle of the element so the user can easily hold and move it,
 * rather than falsely triggering resize / size-increase mode.
 */
const getHitHandle = (bounds, canvasX, canvasY, canvasScale = 1.0, isCircleMark = false) => {
  if (!bounds || !bounds.corners) return null;

  const minDim = Math.min(bounds.width, bounds.height);
  const distToCenter = Math.hypot(canvasX - bounds.cx, canvasY - bounds.cy);

  // 1. Safe inner core check: If cursor is within the central body / middle of the element,
  // it is strictly for MOVING / dragging the element, NEVER resizing!
  const innerMarginX = Math.min(bounds.width * 0.35, Math.max(14, 10 * canvasScale));
  const innerMarginY = Math.min(bounds.height * 0.35, Math.max(14, 10 * canvasScale));
  const isInsideInnerCore =
    canvasX >= bounds.left + innerMarginX &&
    canvasX <= bounds.right - innerMarginX &&
    canvasY >= bounds.top + innerMarginY &&
    canvasY <= bounds.bottom - innerMarginY;

  if (isInsideInnerCore || distToCenter < minDim * 0.40) {
    return null;
  }

  // 2. Maximum handle hit radius around handle center (proportional to visual dot + small touch margin)
  const maxHitRadius = Math.max(9, 7 * canvasScale);
  // Guarantee handle hit radius never exceeds 28% of the element dimension so it never covers the center
  const handleThreshold = Math.min(maxHitRadius, minDim * 0.28);

  // 3. Candidate handles: only include edge center handles (ml, mr, tc, bc) if it's a circle and sufficiently large
  const allowEdgeHandles = isCircleMark && minDim >= 50 * canvasScale;

  let bestCorner = null;
  let minCornerDist = Infinity;

  for (const corner of Object.values(bounds.corners)) {
    if (!allowEdgeHandles && (corner.name === "ml" || corner.name === "mr" || corner.name === "tc" || corner.name === "bc")) {
      continue;
    }
    const d = Math.hypot(canvasX - corner.x, canvasY - corner.y);
    // Must be within threshold, must be closer to the corner handle than to the center of the element, and closest candidate
    if (d <= handleThreshold && d < distToCenter && d < minCornerDist) {
      minCornerDist = d;
      bestCorner = corner;
    }
  }

  return bestCorner;
};

const AnnotationStudio = ({
  answerPaperId,
  paperUrl,
  scoreData,
  studentInfo,
  testInfo,
  serverUrl,
  fetchWithAuth,
  swalSuccess,
  swalError,
  onScoreDataChange,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [loadingText, setLoadingText] = useState("Loading student answer paper...");
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(0.85);
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270 degrees
  const [currentPage, setCurrentPage] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const hoveredCornerRef = useRef(null);

  // Live ScoreData tracking for instant UI & canvas score recalculation
  const [currentScoreData, setCurrentScoreData] = useState(() => sanitizeScoreData(scoreData));
  const currentScoreDataRef = useRef(sanitizeScoreData(scoreData));

  useEffect(() => {
    const clean = sanitizeScoreData(scoreData);
    setCurrentScoreData(clean);
    currentScoreDataRef.current = clean;
  }, [scoreData]);

  // Position overrides persistence
  const storageKey = useMemo(
    () => `grademe_annotation_positions_${answerPaperId || "default"}`,
    [answerPaperId]
  );

  const [markingPositions, setMarkingPositions] = useState(() => {
    const cleanScore = sanitizeScoreData(scoreData);
    // 1. Check if backend has saved positions in annotationPositions or question evaluations
    const serverPositions = extractPositionsFromScoreData(cleanScore);
    if (Object.keys(serverPositions).length > 0) {
      return serverPositions;
    }
    // 2. Fall back to localStorage
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        let changed = false;
        Object.keys(parsed).forEach((k) => {
          // Delete any corrupt page-only aliases like p1-a0 or ann-comment-p1-a0
          if (/p\d+-[as]\d+/.test(k)) {
            delete parsed[k];
            changed = true;
          }
          // Delete any corrupt positional aliases of custom marks
          if (/^(?:ann-comment|ann-loop|ann-mark|step-mark)-(?:q)?\d+-(?:[as])?\d+$/.test(k)) {
            if (parsed[k]?.isCustom || parsed[k]?.annotationId?.includes("custom-") || parsed[k]?.stepId?.includes("custom-")) {
              delete parsed[k];
              changed = true;
            }
          }
          if (parsed[k]?.type === "comment") {
            if (parsed[k].fontFamily === "handwritten") {
              parsed[k].fontFamily = "sans";
              changed = true;
            }
            if (parsed[k].fontSize === 13) {
              parsed[k].fontSize = 11;
              changed = true;
            }
          }
        });

        // Unmerge any duplicate custom comments at identical coordinates
        const seenCoords = new Set();
        Object.keys(parsed).forEach((k) => {
          if (
            (k.startsWith("ann-comment-") || parsed[k]?.type === "comment") &&
            parsed[k]?.xRatio != null &&
            parsed[k]?.yRatio != null
          ) {
            const coordKey = `${Number(parsed[k].xRatio).toFixed(3)},${Number(parsed[k].yRatio).toFixed(3)}_${parsed[k].text || ""}`;
            if (parsed[k]?.isCustom && seenCoords.has(coordKey)) {
              delete parsed[k]; // Reset to natural question layout
              changed = true;
            } else if (parsed[k]?.isCustom) {
              seenCoords.add(coordKey);
            }
          }
        });

        if (changed) {
          try {
            localStorage.setItem(storageKey, JSON.stringify(parsed));
          } catch (_) {}
        }
        return parsed;
      }
      return {};
    } catch (_) {
      return {};
    }
  });

  const [hoveredMarkId, setHoveredMarkId] = useState(null);
  const hoveredMarkIdRef = useRef(null);
  useEffect(() => {
    hoveredMarkIdRef.current = hoveredMarkId;
  }, [hoveredMarkId]);

  const [selectedMarkId, setSelectedMarkId] = useState(null);
  const selectedMarkIdRef = useRef(null);
  useEffect(() => {
    selectedMarkIdRef.current = selectedMarkId;
  }, [selectedMarkId]);

  const [activeTool, setActiveTool] = useState(null);
  const activeToolRef = useRef(null);
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  // Selected target question index for assigning custom markings & evaluations
  const [targetQuestionIndex, setTargetQuestionIndex] = useState(0);
  const targetQuestionIndexRef = useRef(0);
  useEffect(() => {
    targetQuestionIndexRef.current = targetQuestionIndex;
  }, [targetQuestionIndex]);



  // References
  const containerRef = useRef(null);
  const documentBodyRef = useRef(null);
  const canvasRefs = useRef({});
  const baseBuffersRef = useRef({});
  const renderTasksRef = useRef({});
  const markingsByPageRef = useRef({});
  const dragInfoRef = useRef(null);
  const markingPositionsRef = useRef(markingPositions);
  const modifiedQuestionScoresRef = useRef({});
  const isInternalUpdateRef = useRef(false);
  const loadedPaperIdRef = useRef(answerPaperId || null);

  // Baseline saved state snapshot for change tracking (Save buttons disabled until dirty)
  const savedStateSnapshotRef = useRef(serializeStudioState(markingPositions, {}));
  const isInitialSnapshotSetRef = useRef(false);

  if (!isInitialSnapshotSetRef.current && markingPositions && Object.keys(markingPositions).length > 0) {
    savedStateSnapshotRef.current = serializeStudioState(markingPositions, {});
    isInitialSnapshotSetRef.current = true;
  }

  // Undo / Redo history stack
  const [history, setHistory] = useState([markingPositions]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const pushHistory = useCallback(
    (newPositions) => {
      setHistory((prev) => {
        const sliced = prev.slice(0, historyIndex + 1);
        return [...sliced, newPositions];
      });
      setHistoryIndex((idx) => idx + 1);
    },
    [historyIndex]
  );

  useEffect(() => {
    markingPositionsRef.current = markingPositions;
  }, [markingPositions]);

  // Keep state synchronized if a new paper loads or initial scoreData loads from DB
  useEffect(() => {
    // If user is actively dragging or resizing a mark, DO NOT interrupt!
    if (dragInfoRef.current) return;

    // If this update was triggered by internal studio edits, do NOT reset baseline snapshot or history!
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }
    // Also skip if scoreData reference matches our current internal scoreData instance
    if (
      scoreData &&
      currentScoreDataRef.current &&
      scoreData === currentScoreDataRef.current &&
      isInitialSnapshotSetRef.current
    ) {
      return;
    }

    const isNewPaper = loadedPaperIdRef.current !== answerPaperId;
    if (isNewPaper || !isInitialSnapshotSetRef.current) {
      loadedPaperIdRef.current = answerPaperId;
      const cleanScore = sanitizeScoreData(scoreData);
      const serverPositions = extractPositionsFromScoreData(cleanScore);
      if (Object.keys(serverPositions).length > 0 || isNewPaper) {
        setMarkingPositions(serverPositions);
        markingPositionsRef.current = serverPositions;
        savedStateSnapshotRef.current = serializeStudioState(serverPositions, {});
        isInitialSnapshotSetRef.current = true;
        setHistory([serverPositions]);
        setHistoryIndex(0);
        try {
          if (Object.keys(serverPositions).length > 0) {
            localStorage.setItem(storageKey, JSON.stringify(serverPositions));
          }
        } catch (_) {}
      }
    }
  }, [scoreData, storageKey, answerPaperId]);

  // Check if paperUrl is an image
  const isImageFile = useMemo(() => {
    if (!paperUrl) return false;
    const clean = paperUrl.split("?")[0].toLowerCase();
    return /\.(png|jpe?g|webp|gif|bmp)$/.test(clean);
  }, [paperUrl]);

  // 1. Load Raw Student Answer Paper via PDF.js or Image
  useEffect(() => {
    if (!paperUrl) {
      setError("No student answer paper URL found.");
      setLoading(false);
      return;
    }

    if (isImageFile) {
      setTotalPages(1);
      setCurrentPage(1);
      setLoading(false);
      setError(null);
      return;
    }

    let isCancelled = false;
    let activeDoc = null;
    setLoading(true);
    setError(null);
    setLoadingText("Loading student answer paper pages...");

    const loadPdf = async () => {
      try {
        const pdfjs = await ensurePdfJs();
        if (isCancelled) return;

        const loadingTask = pdfjs.getDocument({
          url: paperUrl,
          cMapUrl: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/",
          cMapPacked: true,
        });

        const doc = await loadingTask.promise;
        if (!isCancelled) {
          activeDoc = doc;
          setPdfDoc(doc);
          setTotalPages(doc.numPages || 1);
          setCurrentPage(1);
        } else {
          try {
            doc.destroy();
          } catch (_) {}
        }
      } catch (err) {
        console.error("Failed to load student answer paper PDF:", err);
        if (!isCancelled) {
          setError("Failed to load student answer paper. Please verify file access.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      isCancelled = true;
      if (activeDoc) {
        try {
          activeDoc.destroy();
        } catch (_) {}
      }
      // Cancel any active render tasks
      Object.values(renderTasksRef.current).forEach((task) => {
        try {
          task?.cancel();
        } catch (_) {}
      });
      renderTasksRef.current = {};
      baseBuffersRef.current = {};
      setPdfDoc(null);
    };
  }, [paperUrl, isImageFile]);

  // 2. Fast Redraw of a Page: Blits Answer Paper Buffer + Renders Markings on Top (< 0.5ms)
  const redrawPage = useCallback(
    (
      pageNum,
      hId = hoveredMarkIdRef.current,
      dId = dragInfoRef.current?.markId,
      sId = selectedMarkIdRef.current
    ) => {
      const canvas = canvasRefs.current[pageNum];
      const buffer = baseBuffersRef.current[pageNum];
      if (!canvas || !buffer) return;

      const ctx = canvas.getContext("2d");
      // Blit original student answer paper
      ctx.drawImage(buffer, 0, 0);

      // Render red pen markings on top at their current positions
      const markings = renderRedPenAnnotationsToCanvas(
        canvas,
        pageNum,
        currentScoreDataRef.current || scoreData,
        markingPositionsRef.current,
        {
          clearCanvas: false,
          hoveredMarkId: hId,
          draggingMarkId: dId,
          selectedMarkId: sId,
          interactive: true,
        }
      );

      markingsByPageRef.current[pageNum] = markings || [];
    },
    [scoreData]
  );

  const redrawAllPages = useCallback(() => {
    redrawPage(currentPage);
  }, [currentPage, redrawPage]);

  // Studio: Quick 1-click addition of marks directly onto active page
  const handleAddMarkDirectly = useCallback(
    (toolType, chosenQIdx = null, presetText = "") => {
      const activeScore = currentScoreDataRef.current;
      if (!activeScore?.result?.length) return;
      const pageNum = currentPage;
      const pageMatchIdx = activeScore.result.findIndex(
        (q) =>
          q.pageNumber === pageNum ||
          q.scorePosition?.pageNumber === pageNum ||
          (Array.isArray(q.annotations) && q.annotations.some((a) => a?.pageNumber === pageNum)) ||
          (Array.isArray(q.stepEvaluations) && q.stepEvaluations.some((s) => s?.pageNumber === pageNum))
      );
      const fallbackQIdx = pageMatchIdx >= 0 ? pageMatchIdx : (targetQuestionIndexRef.current >= 0 ? targetQuestionIndexRef.current : 0);
      const targetQIdx =
        chosenQIdx !== null && chosenQIdx !== undefined && chosenQIdx >= 0
          ? chosenQIdx
          : fallbackQIdx;

      setTargetQuestionIndex(targetQIdx);
      targetQuestionIndexRef.current = targetQIdx;

      const targetQ = activeScore.result[targetQIdx];
      const targetQNum = targetQ?.questionNumber || targetQIdx + 1;
      const targetQId = targetQ?._id || `q-${targetQIdx}`;

      // Default center position of current view
      const xRatio = 0.50;
      const yRatio = 0.45;

      let markId = "";
      let newMark = null;

      if (toolType === "tick" || toolType === "cross") {
        const isTick = toolType === "tick";
        const newStepId = `step-custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        markId = `step-mark-${newStepId}`;
        const newStep = {
          _id: newStepId,
          stepText: isTick ? "Instructor checkmark" : "Instructor crossmark",
          status: isTick ? "correct" : "incorrect",
          pageNumber: pageNum,
          customX: xRatio,
          customY: yRatio,
          scale: 1.0,
          deleted: false,
          isCustom: true,
        };
        if (targetQ) {
          targetQ.stepEvaluations = targetQ.stepEvaluations || [];
          targetQ.stepEvaluations.push(newStep);
        }
        newMark = {
          id: markId,
          type: isTick ? "tick" : "cross",
          markType: "step-mark",
          status: isTick ? "correct" : "incorrect",
          label: `${isTick ? "Checkmark" : "Crossmark"} (Q${targetQNum})`,
          xRatio,
          yRatio,
          pageNum,
          questionIndex: targetQIdx,
          questionId: targetQId,
          questionNumber: targetQNum,
          stepId: newStepId,
          stepIndex: targetQ ? targetQ.stepEvaluations.length - 1 : 0,
          scale: 1.0,
          color: "#c53030",
          strokeWidth: 2.0,
          isCustom: true,
          deleted: false,
        };
      } else if (toolType === "comment" || toolType === "stamp") {
        const isStamp = toolType === "stamp";
        const commentText = presetText || (isStamp ? "Well Done! 🌟" : "Good work");
        const newAnnId = `ann-custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        markId = `ann-comment-${newAnnId}`;
        const newAnn = {
          _id: newAnnId,
          annotationType: isStamp ? "praise" : "specific_error",
          comment: commentText,
          pageNumber: pageNum,
          placement: "below",
          commentCustomX: xRatio,
          commentCustomY: yRatio,
          commentScale: 1.0,
          fontSize: isStamp ? 14 : 11,
          fontFamily: "sans",
          deleted: false,
          deletedComment: false,
          isCustom: true,
        };
        if (targetQ) {
          targetQ.annotations = targetQ.annotations || [];
          targetQ.annotations.push(newAnn);
        }
        newMark = {
          id: markId,
          type: "comment",
          markType: "comment",
          label: `${isStamp ? "Stamp" : "Comment"} (Q${targetQNum})`,
          text: commentText,
          xRatio,
          yRatio,
          pageNum,
          questionIndex: targetQIdx,
          questionId: targetQId,
          questionNumber: targetQNum,
          annotationId: newAnnId,
          annotationIndex: targetQ ? targetQ.annotations.length - 1 : 0,
          scale: 1.0,
          fontSize: isStamp ? 14 : 11,
          fontFamily: "sans",
          color: "#c53030",
          strokeWidth: 2.0,
          isCustom: true,
          deleted: false,
        };
      } else if (toolType === "circle" || toolType === "line") {
        const isCircle = toolType === "circle";
        const newAnnId = `ann-custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        markId = `ann-loop-${newAnnId}`;
        const newAnn = {
          _id: newAnnId,
          annotationType: "specific_error",
          comment: "",
          pageNumber: pageNum,
          circleLine: isCircle,
          underline: !isCircle,
          placement: "below",
          customX: xRatio,
          customY: yRatio,
          scale: 1.0,
          strokeWidth: 2.0,
          deleted: false,
          deletedComment: false,
          isCustom: true,
        };
        if (targetQ) {
          targetQ.annotations = targetQ.annotations || [];
          targetQ.annotations.push(newAnn);
        }
        newMark = {
          id: markId,
          type: isCircle ? "circle" : "line",
          markType: "ann-loop",
          label: `${isCircle ? "Circle Loop" : "Underline"} (Q${targetQNum})`,
          xRatio,
          yRatio,
          pageNum,
          questionIndex: targetQIdx,
          questionId: targetQId,
          questionNumber: targetQNum,
          annotationId: newAnnId,
          annotationIndex: targetQ ? targetQ.annotations.length - 1 : 0,
          scale: 1.0,
          color: "#c53030",
          strokeWidth: 2.0,
          isCustom: true,
          deleted: false,
        };
      } else if (toolType === "question-score") {
        markId = `q-score-${targetQNum}`;
        newMark = {
          id: markId,
          type: "question-score",
          markType: "question-score",
          label: `Question ${targetQNum} Score`,
          xRatio,
          yRatio,
          pageNum,
          questionIndex: targetQIdx,
          questionId: targetQId,
          questionNumber: targetQNum,
          score: targetQ?.obtainedScore ?? 0,
          maxScore: targetQ?.maxScore ?? 1,
          scale: 1.0,
          color: "#c53030",
          isCustom: true,
          deleted: false,
        };
        if (targetQ) {
          targetQ.scorePosition = {
            ...(targetQ.scorePosition || {}),
            pageNumber: pageNum,
            customX: xRatio,
            customY: yRatio,
            scale: 1.0,
          };
        }
      }

      if (newMark) {
        const updatedMap = {
          ...markingPositionsRef.current,
          [markId]: newMark,
        };
        markingPositionsRef.current = updatedMap;
        setMarkingPositions(updatedMap);
        pushHistory(updatedMap);
        try {
          localStorage.setItem(storageKey, JSON.stringify(updatedMap));
        } catch (_) {}

        // Synchronize score data
        const updatedScoreObj = {
          ...activeScore,
          result: [...activeScore.result],
        };
        currentScoreDataRef.current = updatedScoreObj;
        setCurrentScoreData(updatedScoreObj);
        isInternalUpdateRef.current = true;
        if (onScoreDataChange) onScoreDataChange(updatedScoreObj);

        setSelectedMarkId(markId);
        selectedMarkIdRef.current = markId;
        redrawPage(pageNum, null, null, markId);
        setShowAddModal(false);
        swalToast("success", "Mark placed on page! Drag to move or drag corners to resize.", 3500);
      }
    },
    [currentPage, onScoreDataChange, pushHistory, redrawPage, storageKey]
  );

  // 3. Render Underlying Answer Paper into Offscreen Buffers Once
  const renderBasePaper = useCallback(async () => {
    const pageNum = currentPage;
    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    // If already rendered in buffer, simply ensure DOM canvas dimensions match and redraw markings
    if (baseBuffersRef.current[pageNum]) {
      const buffer = baseBuffersRef.current[pageNum];
      if (canvas.width !== buffer.width || canvas.height !== buffer.height) {
        canvas.width = buffer.width;
        canvas.height = buffer.height;
      }
      redrawPage(pageNum);
      return;
    }

    if (pdfDoc) {
      try {
        const page = await pdfDoc.getPage(pageNum);
        // Crisp High-DPI scale for answer paper
        const viewport = page.getViewport({ scale: 2.0 });

        // Cancel any existing render task for this page before starting a new one
        if (renderTasksRef.current[pageNum]) {
          try {
            renderTasksRef.current[pageNum].cancel();
          } catch (_) {}
          renderTasksRef.current[pageNum] = null;
        }

        // Always create a fresh offscreen canvas with identity transform
        const buffer = document.createElement("canvas");
        buffer.width = viewport.width;
        buffer.height = viewport.height;

        const bCtx = buffer.getContext("2d", { alpha: false });
        bCtx.setTransform(1, 0, 0, 1, 0, 0);
        bCtx.fillStyle = "#ffffff";
        bCtx.fillRect(0, 0, buffer.width, buffer.height);

        const renderTask = page.render({ canvasContext: bCtx, viewport });
        renderTasksRef.current[pageNum] = renderTask;

        await renderTask.promise;
        renderTasksRef.current[pageNum] = null;

        // Cache the rendered page
        baseBuffersRef.current[pageNum] = buffer;

        // Set DOM canvas dimensions and draw
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        redrawPage(pageNum);
      } catch (err) {
        if (err?.name !== "RenderingCancelledException") {
          console.error(`Error rendering page ${pageNum}:`, err);
        }
      }
    } else if (isImageFile && pageNum === 1) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const w = (img.naturalWidth || 1200) * 1.5;
        const h = (img.naturalHeight || 1600) * 1.5;

        const buffer = document.createElement("canvas");
        buffer.width = w;
        buffer.height = h;

        const bCtx = buffer.getContext("2d");
        bCtx.fillStyle = "#ffffff";
        bCtx.fillRect(0, 0, w, h);
        bCtx.drawImage(img, 0, 0, w, h);

        baseBuffersRef.current[1] = buffer;
        canvas.width = w;
        canvas.height = h;
        redrawPage(1);
      };
      img.src = paperUrl;
    }
  }, [pdfDoc, isImageFile, paperUrl, currentPage, redrawPage]);

  useEffect(() => {
    let isCancelled = false;
    const timer = setTimeout(() => {
      if (!isCancelled) {
        renderBasePaper();
      }
    }, 60);
    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [renderBasePaper]);

  // Helper to mirror alias keys for standard markings
  const mirrorAliases = useCallback((markId, markObj, targetMap) => {
    // Custom marks have unique canonical IDs and should never generate positional aliases
    if (markObj?.isCustom || markId.includes("-custom-") || markId.startsWith("custom-")) {
      return;
    }
    let m = markId.match(/^step-mark-(?:q)?([a-zA-Z0-9._-]+)-(?:s)?(\d+)$/);
    if (m) {
      targetMap[`step-mark-${m[1]}-${m[2]}`] = markObj;
      targetMap[`step-mark-q${m[1]}-s${m[2]}`] = markObj;
    }
    m = markId.match(/^ann-loop-(?:q)?([a-zA-Z0-9._-]+)-(?:a)?(\d+)$/);
    if (m) {
      targetMap[`ann-loop-${m[1]}-${m[2]}`] = markObj;
      targetMap[`ann-loop-q${m[1]}-a${m[2]}`] = markObj;
    }
    m = markId.match(/^ann-mark-(?:q)?([a-zA-Z0-9._-]+)-(?:a)?(\d+)$/);
    if (m) {
      targetMap[`ann-mark-${m[1]}-${m[2]}`] = markObj;
      targetMap[`ann-mark-q${m[1]}-a${m[2]}`] = markObj;
      targetMap[`ann-loop-${m[1]}-${m[2]}`] = markObj;
    }
    m = markId.match(/^ann-comment-(?:q)?([a-zA-Z0-9._-]+)-(?:a)?(\d+)$/);
    if (m) {
      targetMap[`ann-comment-${m[1]}-${m[2]}`] = markObj;
      targetMap[`ann-comment-q${m[1]}-a${m[2]}`] = markObj;
    }
    m = markId.match(/^q-score-(?:q)?([a-zA-Z0-9._-]+)$/);
    if (m) {
      targetMap[`q-score-${m[1]}`] = markObj;
      targetMap[`q-score-q${m[1]}`] = markObj;
    }
  }, []);

  // Coordinate mapper accounting for canvas rotation & zoom
  const getCanvasCoords = useCallback(
    (e, canvas) => {
      if (!canvas) return { canvasX: 0, canvasY: 0 };
      const rect = canvas.getBoundingClientRect();

      if (rotation === 0) {
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
          canvasX: (e.clientX - rect.left) * scaleX,
          canvasY: (e.clientY - rect.top) * scaleY,
        };
      }

      // Handle rotation (90, 180, 270)
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;

      const rad = (-rotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);

      const unrotatedDx = dx * cos - dy * sin;
      const unrotatedDy = dx * sin + dy * cos;

      const isSwapped = rotation === 90 || rotation === 270;
      const unrotatedW = isSwapped ? rect.height : rect.width;
      const unrotatedH = isSwapped ? rect.width : rect.height;

      const elemX = unrotatedDx + unrotatedW / 2;
      const elemY = unrotatedDy + unrotatedH / 2;

      const scaleX = canvas.width / unrotatedW;
      const scaleY = canvas.height / unrotatedH;

      const canvasX = Math.max(0, Math.min(canvas.width, elemX * scaleX));
      const canvasY = Math.max(0, Math.min(canvas.height, elemY * scaleY));

      return { canvasX, canvasY };
    },
    [rotation]
  );

  // 4. Pointer Events for Interactive Marking Drag & Drop & Tool Placement
  const handlePointerDown = (e, pageNum) => {
    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    const { canvasX, canvasY } = getCanvasCoords(e, canvas);

    // 1. Placement Mode: User clicked a tool in toolbox, now click on paper to place
    if (activeToolRef.current) {
      const tool = activeToolRef.current;
      let finalCanvasX = canvasX;
      let finalCanvasY = canvasY;

      if (tool === "comment") {
        const estScale = canvas.width / 600;
        const estW = 180 * estScale;
        const padding = 20 * estScale;
        if (finalCanvasX + estW > canvas.width - padding) {
          finalCanvasX = Math.max(padding, canvas.width - padding - estW);
        }
      }

      const xRatio = Math.max(0.02, Math.min(0.98, +(finalCanvasX / canvas.width).toFixed(4)));
      const yRatio = Math.max(0.02, Math.min(0.98, +(finalCanvasY / canvas.height).toFixed(4)));

      const activeScore = currentScoreDataRef.current || scoreData;
      const targetResult = activeScore?.result ? activeScore.result.map(q => ({
        ...q,
        annotations: Array.isArray(q.annotations) ? [...q.annotations] : [],
        stepEvaluations: Array.isArray(q.stepEvaluations) ? [...q.stepEvaluations] : [],
      })) : [];

      let targetQIdx = targetQuestionIndexRef.current;
      if (targetQIdx < 0 || targetQIdx >= targetResult.length) {
        targetQIdx = 0;
      }
      const targetQ = targetResult[targetQIdx];
      const targetQNum = targetQ?.questionNumber || `${targetQIdx + 1}`;
      const targetQId = targetQ?._id;

      const cy = Math.round(yRatio * 1000);
      const cx = Math.round(xRatio * 1000);

      let markId = "";
      let newMark = null;

      if (tool === "comment") {
        const newAnnId = `ann-custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        markId = `ann-comment-${newAnnId}`;
        const newAnn = {
          _id: newAnnId,
          annotationType: "custom_note",
          comment: "Feedback comment",
          pageNumber: pageNum,
          boundingBox: [
            Math.max(0, cy - 15),
            Math.max(0, cx - 25),
            Math.min(1000, cy + 15),
            Math.min(1000, cx + 25)
          ],
          circleLine: false,
          underline: false,
          placement: "below",
          commentCustomX: xRatio,
          commentCustomY: yRatio,
          commentScale: 1.0,
          fontSize: 11,
          fontFamily: "sans",
          deleted: false,
          deletedComment: false,
          isCustom: true,
        };

        if (targetQ) {
          targetQ.annotations.push(newAnn);
        }

        newMark = {
          id: markId,
          type: "comment",
          markType: "comment",
          label: `Comment (Q${targetQNum})`,
          text: "Feedback comment",
          xRatio,
          yRatio,
          pageNum,
          questionIndex: targetQIdx,
          questionId: targetQId,
          questionNumber: targetQNum,
          annotationId: newAnnId,
          annotationIndex: targetQ ? targetQ.annotations.length - 1 : 0,
          scale: 1.0,
          fontSize: 11,
          fontFamily: "sans",
          color: "#c53030",
          strokeWidth: 2.0,
          isCustom: true,
          deleted: false,
        };
      } else if (tool === "tick" || tool === "cross") {
        const isTick = tool === "tick";
        const newStepId = `step-custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        markId = `step-mark-${newStepId}`;
        const newStep = {
          _id: newStepId,
          stepText: isTick ? "Instructor checkmark" : "Instructor crossmark",
          status: isTick ? "correct" : "incorrect",
          pageNumber: pageNum,
          boundingBox: [
            Math.max(0, cy - 12),
            Math.max(0, cx - 20),
            Math.min(1000, cy + 12),
            Math.min(1000, cx + 20)
          ],
          customX: xRatio,
          customY: yRatio,
          scale: 1.0,
          deleted: false,
          isCustom: true,
        };

        if (targetQ) {
          targetQ.stepEvaluations.push(newStep);
        }

        newMark = {
          id: markId,
          type: isTick ? "tick" : "cross",
          markType: "step-mark",
          status: isTick ? "correct" : "incorrect",
          label: `${isTick ? "Checkmark" : "Crossmark"} (Q${targetQNum})`,
          xRatio,
          yRatio,
          pageNum,
          questionIndex: targetQIdx,
          questionId: targetQId,
          questionNumber: targetQNum,
          stepId: newStepId,
          stepIndex: targetQ ? targetQ.stepEvaluations.length - 1 : 0,
          scale: 1.0,
          color: "#c53030",
          strokeWidth: 2.0,
          isCustom: true,
          deleted: false,
        };
      } else if (tool === "circle" || tool === "line") {
        const isCircle = tool === "circle";
        const newAnnId = `ann-custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        markId = `ann-loop-${newAnnId}`;
        const newAnn = {
          _id: newAnnId,
          annotationType: "specific_error",
          comment: "",
          pageNumber: pageNum,
          boundingBox: [
            Math.max(0, cy - (isCircle ? 15 : 5)),
            Math.max(0, cx - (isCircle ? 25 : 30)),
            Math.min(1000, cy + (isCircle ? 15 : 5)),
            Math.min(1000, cx + (isCircle ? 25 : 30))
          ],
          circleLine: isCircle,
          underline: !isCircle,
          placement: "below",
          customX: xRatio,
          customY: yRatio,
          scale: 1.0,
          strokeWidth: 2.0,
          deleted: false,
          deletedComment: false,
          isCustom: true,
        };

        if (targetQ) {
          targetQ.annotations.push(newAnn);
        }

        newMark = {
          id: markId,
          type: isCircle ? "circle" : "line",
          markType: "ann-loop",
          label: `${isCircle ? "Circle Loop" : "Horizontal Line"} (Q${targetQNum})`,
          xRatio,
          yRatio,
          pageNum,
          questionIndex: targetQIdx,
          questionId: targetQId,
          questionNumber: targetQNum,
          annotationId: newAnnId,
          annotationIndex: targetQ ? targetQ.annotations.length - 1 : 0,
          scale: 1.0,
          color: "#c53030",
          strokeWidth: 2.0,
          isCustom: true,
          deleted: false,
        };
      }

      if (targetQ && targetResult.length > 0) {
        const updatedScoreObj = {
          ...activeScore,
          result: targetResult,
        };
        currentScoreDataRef.current = updatedScoreObj;
        setCurrentScoreData(updatedScoreObj);
        isInternalUpdateRef.current = true;
        if (onScoreDataChange) onScoreDataChange(updatedScoreObj);
      }

      const updated = {
        ...markingPositionsRef.current,
        [markId]: newMark,
      };

      markingPositionsRef.current = updated;
      setMarkingPositions(updated);
      pushHistory(updated);
      try {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (_) {}

      setSelectedMarkId(markId);
      selectedMarkIdRef.current = markId;
      setActiveTool(null);
      activeToolRef.current = null;
      redrawPage(pageNum, null, null, markId);
      return;
    }

    // 1. Check if user clicked a corner resize handle of the ALREADY selected mark!
    if (selectedMarkIdRef.current) {
      const selectedDescriptor = (markingsByPageRef.current[pageNum] || []).find(
        (m) => m.id === selectedMarkIdRef.current
      );
      if (selectedDescriptor) {
        const bounds = getMarkingBounds(selectedDescriptor, canvas);
        if (bounds) {
          const canvasScale = (canvas.width || 600) / 600;
          const isCircleMark =
            selectedDescriptor.type === "circle" ||
            selectedDescriptor.type === "custom-circle" ||
            (selectedDescriptor.markType === "ann-loop" && !selectedDescriptor.label?.toLowerCase().includes("underline")) ||
            String(selectedDescriptor.id || "").startsWith("ann-loop");

          const clickedCorner = getHitHandle(bounds, canvasX, canvasY, canvasScale, isCircleMark);

          if (clickedCorner) {
            try {
              e.target.setPointerCapture(e.pointerId);
            } catch (_) {}

            const existingProps = markingPositionsRef.current[selectedMarkIdRef.current] || {};
            const initialScale = existingProps.scale || selectedDescriptor.scale || 1.0;
            const initialDist = Math.hypot(canvasX - bounds.cx, canvasY - bounds.cy) || 1;
            const canvasW = canvas.width || 600;
            const canvasH = canvas.height || 800;
            const currentXRatio = existingProps.xRatio ?? (selectedDescriptor.xRatio ?? (selectedDescriptor.x != null ? +(selectedDescriptor.x / canvasW).toFixed(4) : 0.5));
            const currentYRatio = existingProps.yRatio ?? (selectedDescriptor.yRatio ?? (selectedDescriptor.y != null ? +(selectedDescriptor.y / canvasH).toFixed(4) : 0.5));

            const initialRx = selectedDescriptor.rx || (bounds.width / 2);
            const initialRy = selectedDescriptor.ry || (bounds.height / 2);

            dragInfoRef.current = {
              mode: "resize",
              pointerId: e.pointerId,
              markId: selectedMarkIdRef.current,
              pageNum,
              corner: clickedCorner.name,
              handleAxis: clickedCorner.axis || "both",
              isCircle: isCircleMark,
              centerX: bounds.cx,
              centerY: bounds.cy,
              initialRx,
              initialRy,
              initialDist,
              initialScale,
              currentXRatio,
              currentYRatio,
              hasMoved: false,
              markType: selectedDescriptor.markType || selectedDescriptor.type,
              questionIndex: selectedDescriptor.questionIndex,
              questionId: selectedDescriptor.questionId,
              stepIndex: selectedDescriptor.stepIndex,
              stepId: selectedDescriptor.stepId,
              annotationIndex: selectedDescriptor.annotationIndex,
              annotationId: selectedDescriptor.annotationId,
            };
            canvas.style.cursor = clickedCorner.cursor;
            return;
          }

          // User clicked inside the selected mark's body/bounds (NOT on a handle)!
          // Allow holding and moving directly!
          const isInsideSelectedBounds =
            canvasX >= bounds.left - 4 &&
            canvasX <= bounds.right + 4 &&
            canvasY >= bounds.top - 4 &&
            canvasY <= bounds.bottom + 4;

          if (isInsideSelectedBounds) {
            try {
              e.target.setPointerCapture(e.pointerId);
            } catch (_) {}

            dragInfoRef.current = {
              mode: "move",
              pointerId: e.pointerId,
              markId: selectedDescriptor.id,
              pageNum,
              startMarkX: selectedDescriptor.x,
              startMarkY: selectedDescriptor.y,
              clickCanvasX: canvasX,
              clickCanvasY: canvasY,
              hasMoved: false,
              markType: selectedDescriptor.markType || selectedDescriptor.type,
              questionIndex: selectedDescriptor.questionIndex,
              questionId: selectedDescriptor.questionId,
              stepIndex: selectedDescriptor.stepIndex,
              stepId: selectedDescriptor.stepId,
              annotationIndex: selectedDescriptor.annotationIndex,
              annotationId: selectedDescriptor.annotationId,
              boxW: selectedDescriptor.boxW || 0,
              boxH: selectedDescriptor.boxH || 0,
            };

            setHoveredMarkId(selectedDescriptor.id);
            redrawPage(pageNum, selectedDescriptor.id, selectedDescriptor.id, selectedDescriptor.id);
            canvas.style.cursor = "grabbing";
            return;
          }
        }
      }
    }

    // 2. Select & Drag Mode
    const markings = markingsByPageRef.current[pageNum] || [];
    const hit = hitTestMarking(markings, canvasX, canvasY);

    if (hit) {
      try {
        e.target.setPointerCapture(e.pointerId);
      } catch (_) {}

      dragInfoRef.current = {
        mode: "move",
        pointerId: e.pointerId,
        markId: hit.id,
        pageNum,
        startMarkX: hit.x,
        startMarkY: hit.y,
        clickCanvasX: canvasX,
        clickCanvasY: canvasY,
        hasMoved: false,
        markType: hit.markType || hit.type,
        questionIndex: hit.questionIndex,
        questionId: hit.questionId,
        stepIndex: hit.stepIndex,
        stepId: hit.stepId,
        annotationIndex: hit.annotationIndex,
        annotationId: hit.annotationId,
        boxW: hit.boxW || 0,
        boxH: hit.boxH || 0,
      };

      setSelectedMarkId(hit.id);
      selectedMarkIdRef.current = hit.id;
      setHoveredMarkId(hit.id);
      redrawPage(pageNum, hit.id, hit.id, hit.id);
      canvas.style.cursor = "grabbing";
    } else {
      // Clicked on blank area -> deselect
      if (selectedMarkIdRef.current) {
        setSelectedMarkId(null);
        selectedMarkIdRef.current = null;
        redrawPage(pageNum, null, null, null);
      }
    }
  };

  const handlePointerMove = (e, pageNum) => {
    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    const { canvasX, canvasY } = getCanvasCoords(e, canvas);

    // A0. Resizing active marking via handle
    if (dragInfoRef.current?.mode === "resize" && dragInfoRef.current.pageNum === pageNum) {
      const drag = dragInfoRef.current;
      const canvasW = canvas.width || 600;
      const canvasH = canvas.height || 800;
      const canvasScale = canvasW / 600;

      if (drag.isCircle) {
        // Independent width and height resizing for circle / loop
        const existingProps = markingPositionsRef.current[drag.markId] || {};
        let updatedRadiusRatio = existingProps.radiusRatio;
        let updatedHeightRatio = existingProps.heightRatio;

        if (drag.handleAxis === "width" || drag.corner === "ml" || drag.corner === "mr") {
          const newRx = Math.max(12 * canvasScale, Math.abs(canvasX - drag.centerX));
          updatedRadiusRatio = +(newRx / canvasW).toFixed(4);
          drag.hasMoved = true;
        } else if (drag.handleAxis === "height" || drag.corner === "tc" || drag.corner === "bc") {
          const newRy = Math.max(8 * canvasScale, Math.abs(canvasY - drag.centerY));
          updatedHeightRatio = +( (2 * newRy) / canvasH ).toFixed(4);
          drag.hasMoved = true;
        } else {
          // Corner handles: adjust both width and height independently
          const newRx = Math.max(12 * canvasScale, Math.abs(canvasX - drag.centerX));
          const newRy = Math.max(8 * canvasScale, Math.abs(canvasY - drag.centerY));
          updatedRadiusRatio = +(newRx / canvasW).toFixed(4);
          updatedHeightRatio = +( (2 * newRy) / canvasH ).toFixed(4);
          drag.hasMoved = true;
        }

        const newPos = {
          ...existingProps,
          xRatio: existingProps.xRatio ?? drag.currentXRatio,
          yRatio: existingProps.yRatio ?? drag.currentYRatio,
          radiusRatio: updatedRadiusRatio,
          heightRatio: updatedHeightRatio,
          scale: 1.0,
          pageNum,
          markType: existingProps.markType || drag.markType,
          type: existingProps.type || drag.markType,
          questionIndex: existingProps.questionIndex ?? drag.questionIndex,
          questionId: existingProps.questionId ?? drag.questionId,
          annotationIndex: existingProps.annotationIndex ?? drag.annotationIndex,
          annotationId: existingProps.annotationId ?? drag.annotationId,
          stepIndex: existingProps.stepIndex ?? drag.stepIndex,
          stepId: existingProps.stepId ?? drag.stepId,
        };
        const newMap = {
          ...markingPositionsRef.current,
          [drag.markId]: newPos,
        };

        mirrorAliases(drag.markId, newPos, newMap);
        markingPositionsRef.current = newMap;
        setMarkingPositions(newMap);

        redrawPage(pageNum, drag.markId, drag.markId, drag.markId);
        canvas.style.cursor = drag.corner === "ml" || drag.corner === "mr"
          ? "ew-resize"
          : (drag.corner === "tc" || drag.corner === "bc"
            ? "ns-resize"
            : (drag.corner === "tl" || drag.corner === "br" ? "nwse-resize" : "nesw-resize"));
        return;
      }

      // Non-circle markings: standard uniform scaling
      const curDist = Math.hypot(canvasX - (drag.centerX || 0), canvasY - (drag.centerY || 0));
      const initDist = (drag.initialDist && !isNaN(drag.initialDist) && drag.initialDist > 0) ? drag.initialDist : 1;
      const rawRatio = isNaN(curDist) ? 1 : curDist / initDist;
      const initScale = (drag.initialScale && !isNaN(drag.initialScale)) ? drag.initialScale : 1.0;
      let calculatedScale = +(initScale * rawRatio).toFixed(2);
      if (isNaN(calculatedScale) || calculatedScale <= 0) calculatedScale = 1.0;
      const newScale = Math.max(0.3, Math.min(3.0, calculatedScale));

      if (Math.abs(newScale - initScale) > 0.02) {
        drag.hasMoved = true;
      }

      const existingProps = markingPositionsRef.current[drag.markId] || {};
      const newPos = {
        ...existingProps,
        xRatio: existingProps.xRatio ?? drag.currentXRatio,
        yRatio: existingProps.yRatio ?? drag.currentYRatio,
        scale: newScale,
        pageNum,
        markType: existingProps.markType || drag.markType,
        type: existingProps.type || drag.markType,
        questionIndex: existingProps.questionIndex ?? drag.questionIndex,
        questionId: existingProps.questionId ?? drag.questionId,
        annotationIndex: existingProps.annotationIndex ?? drag.annotationIndex,
        annotationId: existingProps.annotationId ?? drag.annotationId,
        stepIndex: existingProps.stepIndex ?? drag.stepIndex,
        stepId: existingProps.stepId ?? drag.stepId,
      };
      const newMap = {
        ...markingPositionsRef.current,
        [drag.markId]: newPos,
      };

      mirrorAliases(drag.markId, newPos, newMap);
      markingPositionsRef.current = newMap;
      setMarkingPositions(newMap);

      redrawPage(pageNum, drag.markId, drag.markId, drag.markId);
      canvas.style.cursor = drag.corner === "tl" || drag.corner === "br" ? "nwse-resize" : "nesw-resize";
      return;
    }

    // A1. Moving / Dragging active marking
    if (dragInfoRef.current && dragInfoRef.current.pageNum === pageNum) {
      const drag = dragInfoRef.current;
      const dx = canvasX - drag.clickCanvasX;
      const dy = canvasY - drag.clickCanvasY;

      if (Math.hypot(dx, dy) > 3) {
        drag.hasMoved = true;
      }

      let newX = drag.startMarkX + dx;
      let newY = drag.startMarkY + dy;

      const isComment =
        drag.markType === "comment" ||
        drag.markType === "custom-comment" ||
        drag.markId?.includes("comment");

      const padding = 16;
      const estScale = canvas.width / 600;
      const boxW = drag.boxW || (isComment ? 180 * estScale : 0);
      const boxH = drag.boxH || (isComment ? 30 * estScale : 0);

      const maxX = Math.max(padding, canvas.width - padding - boxW);
      const maxY = Math.max(padding, canvas.height - padding - boxH);

      newX = Math.max(padding, Math.min(maxX, newX));
      newY = Math.max(padding, Math.min(maxY, newY));

      const xRatio = +(newX / canvas.width).toFixed(4);
      const yRatio = +(newY / canvas.height).toFixed(4);

      const existingProps = markingPositionsRef.current[drag.markId] || {};
      const newPos = {
        ...existingProps,
        xRatio,
        yRatio,
        pageNum,
        markType: existingProps.markType || drag.markType,
        type: existingProps.type || drag.markType,
        questionIndex: existingProps.questionIndex ?? drag.questionIndex,
        questionId: existingProps.questionId ?? drag.questionId,
        annotationIndex: existingProps.annotationIndex ?? drag.annotationIndex,
        annotationId: existingProps.annotationId ?? drag.annotationId,
        stepIndex: existingProps.stepIndex ?? drag.stepIndex,
        stepId: existingProps.stepId ?? drag.stepId,
      };
      const newMap = {
        ...markingPositionsRef.current,
        [drag.markId]: newPos,
      };

      mirrorAliases(drag.markId, newPos, newMap);

      markingPositionsRef.current = newMap;

      redrawPage(pageNum, drag.markId, drag.markId, drag.markId);
      canvas.style.cursor = "grabbing";
      return;
    }

    // B. If tool is active, keep cursor as crosshair
    if (activeToolRef.current) {
      canvas.style.cursor = "crosshair";
      return;
    }

    // C. Check if hovering over a corner handle or body of the selected mark
    if (selectedMarkIdRef.current && !dragInfoRef.current) {
      const selectedDescriptor = (markingsByPageRef.current[pageNum] || []).find(
        (m) => m.id === selectedMarkIdRef.current
      );
      if (selectedDescriptor) {
        const bounds = getMarkingBounds(selectedDescriptor, canvas);
        if (bounds) {
          const canvasScale = (canvas.width || 600) / 600;
          const isCircleMark =
            selectedDescriptor.type === "circle" ||
            selectedDescriptor.type === "custom-circle" ||
            (selectedDescriptor.markType === "ann-loop" && !selectedDescriptor.label?.toLowerCase().includes("underline")) ||
            String(selectedDescriptor.id || "").startsWith("ann-loop");

          const hCorner = getHitHandle(bounds, canvasX, canvasY, canvasScale, isCircleMark);
          if (hCorner) {
            canvas.style.cursor = hCorner.cursor;
            hoveredCornerRef.current = hCorner;
            return;
          }
          hoveredCornerRef.current = null;

          // If hovering over the middle / body of the selected mark -> show "grab" cursor!
          const isInsideSelectedBounds =
            canvasX >= bounds.left - 4 &&
            canvasX <= bounds.right + 4 &&
            canvasY >= bounds.top - 4 &&
            canvasY <= bounds.bottom + 4;

          if (isInsideSelectedBounds) {
            canvas.style.cursor = "grab";
            if (hoveredMarkId !== selectedDescriptor.id) {
              setHoveredMarkId(selectedDescriptor.id);
              redrawPage(pageNum, selectedDescriptor.id, null, selectedMarkIdRef.current);
            }
            return;
          }
        }
      }
    }
    hoveredCornerRef.current = null;

    // D. Hover Testing over markings
    const markings = markingsByPageRef.current[pageNum] || [];
    const hit = hitTestMarking(markings, canvasX, canvasY);

    if (hit) {
      canvas.style.cursor = "grab";
      if (hoveredMarkId !== hit.id) {
        setHoveredMarkId(hit.id);
        redrawPage(pageNum, hit.id, null, selectedMarkIdRef.current);
      }
    } else {
      canvas.style.cursor = "default";
      if (hoveredMarkId !== null) {
        setHoveredMarkId(null);
        redrawPage(pageNum, null, null, selectedMarkIdRef.current);
      }
    }
  };

  const handlePointerUp = (e, pageNum) => {
    if (dragInfoRef.current) {
      try {
        e.target.releasePointerCapture(dragInfoRef.current.pointerId);
      } catch (_) {}

      const drag = dragInfoRef.current;
      const markId = drag.markId;
      const hadMoved = drag.hasMoved;
      dragInfoRef.current = null;

      const updated = { ...markingPositionsRef.current };
      setMarkingPositions(updated);
      if (hadMoved) {
        pushHistory(updated);
        try {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch (_) {}

        // Synchronize updated position & scale directly into currentScoreData
        const finalPos = updated[markId];
        if (finalPos) {
          const safeXRatio = finalPos.xRatio ?? drag.currentXRatio ?? 0.5;
          const safeYRatio = finalPos.yRatio ?? drag.currentYRatio ?? 0.5;
          const safeScale = (finalPos.scale && !isNaN(finalPos.scale)) ? finalPos.scale : 1.0;

          finalPos.xRatio = safeXRatio;
          finalPos.yRatio = safeYRatio;
          finalPos.scale = safeScale;

          const prevScoreData = currentScoreDataRef.current || scoreData;
          if (prevScoreData && prevScoreData.result) {
            let updatedScoreData = { ...prevScoreData };
            const mType = drag.markType || "";
            if (mType === "top-score" || markId === "top-score-stamp") {
              updatedScoreData.topScorePosition = {
                ...(updatedScoreData.topScorePosition || {}),
                pageNumber: 1,
                customX: safeXRatio,
                customY: safeYRatio,
                scale: safeScale,
                deleted: Boolean(finalPos.deleted),
              };
            } else if (mType === "question-score" || markId.startsWith("q-score")) {
              const qIdx = drag.questionIndex ?? updatedScoreData.result.findIndex(q => String(q._id) === String(drag.questionId));
              if (qIdx >= 0 && updatedScoreData.result[qIdx]) {
                const updatedResult = [...updatedScoreData.result];
                updatedResult[qIdx] = {
                  ...updatedResult[qIdx],
                  scorePosition: {
                    ...(updatedResult[qIdx].scorePosition || {}),
                    pageNumber: pageNum || 1,
                    customX: safeXRatio,
                    customY: safeYRatio,
                    scale: safeScale,
                    deleted: Boolean(finalPos.deleted),
                  }
                };
                updatedScoreData.result = updatedResult;
              }
            } else if (mType === "step-mark" || markId.startsWith("step-mark") || mType === "tick" || mType === "cross") {
              const qIdx = drag.questionIndex ?? (finalPos.questionIndex ?? (markingPositionsRef.current[markId]?.questionIndex ?? (drag.questionId ? updatedScoreData.result.findIndex(q => String(q._id) === String(drag.questionId)) : -1)));
              if (qIdx >= 0 && updatedScoreData.result[qIdx]?.stepEvaluations) {
                let sIdx = drag.stepIndex ?? (finalPos.stepIndex ?? markingPositionsRef.current[markId]?.stepIndex);
                if (sIdx === undefined || sIdx < 0) {
                  sIdx = updatedScoreData.result[qIdx].stepEvaluations.findIndex(s => s._id && markId.includes(String(s._id)));
                }
                if (sIdx >= 0 && updatedScoreData.result[qIdx].stepEvaluations[sIdx]) {
                  const updatedResult = [...updatedScoreData.result];
                  const updatedSteps = [...updatedResult[qIdx].stepEvaluations];
                  updatedSteps[sIdx] = {
                    ...updatedSteps[sIdx],
                    pageNumber: pageNum || 1,
                    customX: safeXRatio,
                    customY: safeYRatio,
                    scale: safeScale,
                    deleted: Boolean(finalPos.deleted),
                  };
                  updatedResult[qIdx] = { ...updatedResult[qIdx], stepEvaluations: updatedSteps };
                  updatedScoreData.result = updatedResult;
                }
              }
            } else if (mType === "ann-loop" || markId.startsWith("ann-loop") || mType === "circle" || mType === "line") {
              const qIdx = drag.questionIndex ?? (finalPos.questionIndex ?? (markingPositionsRef.current[markId]?.questionIndex ?? (drag.questionId ? updatedScoreData.result.findIndex(q => String(q._id) === String(drag.questionId)) : -1)));
              if (qIdx >= 0 && updatedScoreData.result[qIdx]?.annotations) {
                let aIdx = drag.annotationIndex ?? (finalPos.annotationIndex ?? markingPositionsRef.current[markId]?.annotationIndex);
                if (aIdx === undefined || aIdx < 0) {
                  aIdx = updatedScoreData.result[qIdx].annotations.findIndex(a => a._id && markId.includes(String(a._id)));
                }
                if (aIdx >= 0 && updatedScoreData.result[qIdx].annotations[aIdx]) {
                  const updatedResult = [...updatedScoreData.result];
                  const updatedAnns = [...updatedResult[qIdx].annotations];
                  updatedAnns[aIdx] = {
                    ...updatedAnns[aIdx],
                    pageNumber: pageNum || 1,
                    customX: safeXRatio,
                    customY: safeYRatio,
                    scale: safeScale,
                    radiusRatio: finalPos.radiusRatio,
                    heightRatio: finalPos.heightRatio,
                    deleted: Boolean(finalPos.deleted),
                  };
                  updatedResult[qIdx] = { ...updatedResult[qIdx], annotations: updatedAnns };
                  updatedScoreData.result = updatedResult;
                }
              }
            } else if (mType === "comment" || markId.startsWith("ann-comment") || markId.includes("comment")) {
              const qIdx = drag.questionIndex ?? (finalPos.questionIndex ?? (markingPositionsRef.current[markId]?.questionIndex ?? (drag.questionId ? updatedScoreData.result.findIndex(q => String(q._id) === String(drag.questionId)) : -1)));
              if (qIdx >= 0 && updatedScoreData.result[qIdx]?.annotations) {
                let aIdx = drag.annotationIndex ?? (finalPos.annotationIndex ?? markingPositionsRef.current[markId]?.annotationIndex);
                if (aIdx === undefined || aIdx < 0) {
                  aIdx = updatedScoreData.result[qIdx].annotations.findIndex(a => a._id && markId.includes(String(a._id)));
                }
                if (aIdx >= 0 && updatedScoreData.result[qIdx].annotations[aIdx]) {
                  const updatedResult = [...updatedScoreData.result];
                  const updatedAnns = [...updatedResult[qIdx].annotations];
                  updatedAnns[aIdx] = {
                    ...updatedAnns[aIdx],
                    pageNumber: pageNum || 1,
                    commentCustomX: safeXRatio,
                    commentCustomY: safeYRatio,
                    commentScale: safeScale,
                    deletedComment: Boolean(finalPos.deleted),
                  };
                  updatedResult[qIdx] = { ...updatedResult[qIdx], annotations: updatedAnns };
                  updatedScoreData.result = updatedResult;
                }
              }
            }
            currentScoreDataRef.current = updatedScoreData;
            setCurrentScoreData(updatedScoreData);
            isInternalUpdateRef.current = true;
            if (onScoreDataChange) onScoreDataChange(updatedScoreData);
          }
        }
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (_) {}

      const canvas = canvasRefs.current[pageNum];
      if (canvas) canvas.style.cursor = activeToolRef.current ? "crosshair" : "grab";
      redrawPage(pageNum, markId, null, markId);
    }
  };

  const handlePointerLeave = (e, pageNum) => {
    if (!dragInfoRef.current) {
      setHoveredMarkId(null);
      redrawPage(pageNum, null, null, selectedMarkIdRef.current);
      const canvas = canvasRefs.current[pageNum];
      if (canvas) canvas.style.cursor = activeToolRef.current ? "crosshair" : "default";
    }
  };

  // Undo and Redo handlers
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIdx = historyIndex - 1;
      const targetState = history[newIdx];
      setHistoryIndex(newIdx);
      markingPositionsRef.current = targetState;
      setMarkingPositions(targetState);
      try {
        localStorage.setItem(storageKey, JSON.stringify(targetState));
      } catch (_) {}
      redrawAllPages();
    }
  }, [historyIndex, history, storageKey, redrawAllPages]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIdx = historyIndex + 1;
      const targetState = history[newIdx];
      setHistoryIndex(newIdx);
      markingPositionsRef.current = targetState;
      setMarkingPositions(targetState);
      try {
        localStorage.setItem(storageKey, JSON.stringify(targetState));
      } catch (_) {}
      redrawAllPages();
    }
  }, [historyIndex, history, storageKey, redrawAllPages]);

  // Mark property update handler
  const handleUpdateSelectedMark = useCallback(
    (updates) => {
      const id = selectedMarkIdRef.current;
      if (!id) return;

      const currentMark = markingPositionsRef.current[id] || {};
      const updatedMark = {
        ...currentMark,
        ...updates,
        id,
      };

      const updatedMap = {
        ...markingPositionsRef.current,
        [id]: updatedMark,
      };

      mirrorAliases(id, updatedMark, updatedMap);

      markingPositionsRef.current = updatedMap;
      setMarkingPositions(updatedMap);
      pushHistory(updatedMap);
      try {
        localStorage.setItem(storageKey, JSON.stringify(updatedMap));
      } catch (_) {}

      // Synchronize mark updates directly to currentScoreData (score, position, scale, text, strokeWidth)
      const prev = currentScoreDataRef.current;
      if (prev?.result) {
        const selectedDescriptor = (markingsByPageRef.current[currentPage] || []).find(m => m.id === id);
        let updatedScoreData = { ...prev };
        let updatedResult = [...prev.result];
        let changed = false;

        const targetQIdx = selectedDescriptor?.questionIndex ?? (currentMark?.questionIndex ?? (currentMark?.qIdx ?? -1));
        const targetQ = targetQIdx >= 0 ? updatedResult[targetQIdx] : null;

        if (targetQ) {
          const mType = selectedDescriptor?.markType || currentMark?.type || "";

          if (mType === "question-score" || id.startsWith("q-score")) {
            updatedResult[targetQIdx] = {
              ...targetQ,
              ...(updates.score !== undefined ? { obtainedScore: Number(updates.score) } : {}),
              scorePosition: {
                ...(targetQ.scorePosition || {}),
                ...(updates.scale !== undefined ? { scale: updates.scale } : {}),
                ...(updates.xRatio !== undefined ? { customX: updates.xRatio } : {}),
                ...(updates.yRatio !== undefined ? { customY: updates.yRatio } : {}),
              }
            };
            changed = true;
          } else if (mType === "step-mark" || id.startsWith("step-mark") || mType === "tick" || mType === "cross") {
            let sIdx = selectedDescriptor?.stepIndex ?? currentMark?.stepIndex;
            if (sIdx === undefined || sIdx < 0) {
              sIdx = targetQ.stepEvaluations?.findIndex(s => s._id && id.includes(String(s._id)));
            }
            if (sIdx >= 0 && targetQ.stepEvaluations?.[sIdx]) {
              const updatedSteps = [...targetQ.stepEvaluations];
              updatedSteps[sIdx] = {
                ...updatedSteps[sIdx],
                ...(updates.scale !== undefined ? { scale: updates.scale } : {}),
                ...(updates.xRatio !== undefined ? { customX: updates.xRatio } : {}),
                ...(updates.yRatio !== undefined ? { customY: updates.yRatio } : {}),
                ...(updates.status !== undefined ? {
                  status: updates.status,
                  stepText: updates.status === "correct" ? "Instructor checkmark" : "Instructor crossmark",
                } : {}),
              };
              updatedResult[targetQIdx] = { ...targetQ, stepEvaluations: updatedSteps };
              changed = true;
            }
          } else if (mType === "ann-loop" || id.startsWith("ann-loop") || mType === "circle" || mType === "line") {
            let aIdx = selectedDescriptor?.annotationIndex ?? currentMark?.annotationIndex;
            if (aIdx === undefined || aIdx < 0) {
              aIdx = targetQ.annotations?.findIndex(a => a._id && id.includes(String(a._id)));
            }
            if (aIdx >= 0 && targetQ.annotations?.[aIdx]) {
              const updatedAnns = [...targetQ.annotations];
              updatedAnns[aIdx] = {
                ...updatedAnns[aIdx],
                ...(updates.scale !== undefined ? { scale: updates.scale } : {}),
                ...(updates.strokeWidth !== undefined ? { strokeWidth: updates.strokeWidth } : {}),
                ...(updates.xRatio !== undefined ? { customX: updates.xRatio } : {}),
                ...(updates.yRatio !== undefined ? { customY: updates.yRatio } : {}),
              };
              updatedResult[targetQIdx] = { ...targetQ, annotations: updatedAnns };
              changed = true;
            }
          } else if (mType === "comment" || id.startsWith("ann-comment") || id.includes("comment")) {
            let aIdx = selectedDescriptor?.annotationIndex ?? currentMark?.annotationIndex;
            if (aIdx === undefined || aIdx < 0) {
              aIdx = targetQ.annotations?.findIndex(a => a._id && id.includes(String(a._id)));
            }
            if (aIdx >= 0 && targetQ.annotations?.[aIdx]) {
              const updatedAnns = [...targetQ.annotations];
              updatedAnns[aIdx] = {
                ...updatedAnns[aIdx],
                ...(updates.text !== undefined ? { comment: updates.text } : {}),
                ...(updates.fontSize !== undefined ? { fontSize: updates.fontSize } : {}),
                ...(updates.fontFamily !== undefined ? { fontFamily: updates.fontFamily } : {}),
                ...(updates.scale !== undefined ? { commentScale: updates.scale } : {}),
                ...(updates.xRatio !== undefined ? { commentCustomX: updates.xRatio } : {}),
                ...(updates.yRatio !== undefined ? { commentCustomY: updates.yRatio } : {}),
              };
              updatedResult[targetQIdx] = { ...targetQ, annotations: updatedAnns };
              changed = true;
            }
          }
        }

        if (id === "top-score-stamp" || selectedDescriptor?.markType === "top-score") {
          updatedScoreData.topScorePosition = {
            ...(updatedScoreData.topScorePosition || {}),
            pageNumber: 1,
            ...(updates.scale !== undefined ? { scale: updates.scale } : {}),
            ...(updates.xRatio !== undefined ? { customX: updates.xRatio } : {}),
            ...(updates.yRatio !== undefined ? { customY: updates.yRatio } : {}),
          };
          changed = true;
        }

        if (changed) {
          const newTotalScore = updatedResult.reduce((sum, q) => sum + (Number(q.obtainedScore) || 0), 0);
          updatedScoreData = {
            ...updatedScoreData,
            result: updatedResult,
            totalScore: Math.round(newTotalScore * 100) / 100,
          };
          currentScoreDataRef.current = updatedScoreData;
          setCurrentScoreData(updatedScoreData);
          isInternalUpdateRef.current = true;
          if (onScoreDataChange) onScoreDataChange(updatedScoreData);
        }
      }

      // If a score value was updated, synchronize currentScoreData
      if (updates.score !== undefined) {
        const prev = currentScoreDataRef.current;
        if (prev?.result) {
          const targetQKey = String(updates.questionNumber || updates.qNum || updates.qKey || (id || "").replace(/^q-score-(?:q)?/, "")).trim();
          const targetQId = updates.resultItemId || updates.questionId;
          
          let matchedQuestionObj = null;
          const updatedResult = prev.result.map((q, idx) => {
            const qNumClean = String(q.questionNumber || idx + 1).trim();
            const qId = q._id ? String(q._id) : null;
            if (
              (targetQId && qId === String(targetQId)) ||
              qNumClean.toLowerCase() === targetQKey.toLowerCase() ||
              qNumClean.replace(/^q(?:uestion)?[\s.-]*/i, "").toLowerCase() === targetQKey.toLowerCase() ||
              String(idx + 1) === targetQKey ||
              (qId && qId === targetQKey)
            ) {
              matchedQuestionObj = {
                ...q,
                obtainedScore: Number(updates.score),
              };
              return matchedQuestionObj;
            }
            return q;
          });

          if (matchedQuestionObj) {
            const trackKey = matchedQuestionObj._id ? String(matchedQuestionObj._id) : targetQKey;
            modifiedQuestionScoresRef.current[trackKey] = {
              resultItemId: matchedQuestionObj._id,
              questionNumber: matchedQuestionObj.questionNumber || targetQKey,
              obtainedScore: Number(updates.score),
              maxScore: matchedQuestionObj.maxScore,
              feedback: matchedQuestionObj.feedback,
              studentAnswer: matchedQuestionObj.studentAnswer,
            };
          }

          const newTotalScore = updatedResult.reduce(
            (sum, q) => sum + (Number(q.obtainedScore) || 0),
            0
          );

          const newScoreData = {
            ...prev,
            result: updatedResult,
            totalScore: Math.round(newTotalScore * 100) / 100,
          };
          
          currentScoreDataRef.current = newScoreData;
          setCurrentScoreData(newScoreData);
          
          isInternalUpdateRef.current = true;
          if (onScoreDataChange) {
            onScoreDataChange(newScoreData);
          }
        }
      }

      const targetPage = updatedMark.pageNum || currentPage;
      redrawPage(targetPage, null, null, id);
      // If score changed and mark is not on Page 1, also redraw Page 1 so the top score stamp updates immediately
      if (updates.score !== undefined && targetPage !== 1) {
        redrawPage(1, null, null, null);
      }
    },
    [currentPage, redrawPage, mirrorAliases, storageKey, pushHistory, onScoreDataChange]
  );

  // Mark deletion handler (soft-delete with restore/undo)
  const handleDeleteSelectedMark = useCallback(async () => {
    const id = selectedMarkIdRef.current;
    if (!id) return;

    const currentMark = markingPositionsRef.current[id] || {};
    const markingsOnPage = markingsByPageRef.current[currentPage] || [];
    const descriptor = markingsOnPage.find((m) => m.id === id);

    // Score circles (top score stamp & question score circles) cannot be deleted
    const mType = (descriptor?.type || currentMark.type || "").toLowerCase();
    const mId = String(id).toLowerCase();
    if (
      mType === "top-score" ||
      mType === "question-score" ||
      mType.includes("score") ||
      mId.includes("top-score") ||
      mId.startsWith("q-score") ||
      mId.includes("score")
    ) {
      return;
    }

    const updatedMark = {
      ...currentMark,
      deleted: true,
      id,
    };

    // Mark deleted directly on currentScoreData
    const prevScore = currentScoreDataRef.current;
    if (prevScore?.result) {
      const selectedDescriptor = (markingsByPageRef.current[currentPage] || []).find(m => m.id === id);
      const targetQIdx = selectedDescriptor?.questionIndex ?? (currentMark?.questionIndex ?? (currentMark?.qIdx ?? -1));
      if (targetQIdx >= 0 && prevScore.result[targetQIdx]) {
        const updatedResult = prevScore.result.map(q => ({
          ...q,
          annotations: Array.isArray(q.annotations) ? [...q.annotations] : [],
          stepEvaluations: Array.isArray(q.stepEvaluations) ? [...q.stepEvaluations] : [],
        }));
        const targetQ = updatedResult[targetQIdx];
        const mType = selectedDescriptor?.markType || currentMark?.type || "";

        if (mType === "step-mark" || id.startsWith("step-mark") || mType === "tick" || mType === "cross") {
          let sIdx = selectedDescriptor?.stepIndex ?? currentMark?.stepIndex;
          if (sIdx === undefined || sIdx < 0) {
            sIdx = targetQ.stepEvaluations?.findIndex(s => s._id && id.includes(String(s._id)));
          }
          if (sIdx >= 0 && targetQ.stepEvaluations?.[sIdx]) {
            targetQ.stepEvaluations[sIdx] = { ...targetQ.stepEvaluations[sIdx], deleted: true };
          }
        } else if (mType === "ann-loop" || id.startsWith("ann-loop") || mType === "circle" || mType === "line") {
          let aIdx = selectedDescriptor?.annotationIndex ?? currentMark?.annotationIndex;
          if (aIdx === undefined || aIdx < 0) {
            aIdx = targetQ.annotations?.findIndex(a => a._id && id.includes(String(a._id)));
          }
          if (aIdx >= 0 && targetQ.annotations?.[aIdx]) {
            targetQ.annotations[aIdx] = { ...targetQ.annotations[aIdx], deleted: true };
          }
        } else if (mType === "comment" || id.startsWith("ann-comment") || id.includes("comment")) {
          let aIdx = selectedDescriptor?.annotationIndex ?? currentMark?.annotationIndex;
          if (aIdx === undefined || aIdx < 0) {
            aIdx = targetQ.annotations?.findIndex(a => a._id && id.includes(String(a._id)));
          }
          if (aIdx >= 0 && targetQ.annotations?.[aIdx]) {
            targetQ.annotations[aIdx] = { ...targetQ.annotations[aIdx], deletedComment: true, deleted: true };
          }
        }
        const updatedScoreData = { ...prevScore, result: updatedResult };
        currentScoreDataRef.current = updatedScoreData;
        setCurrentScoreData(updatedScoreData);
        isInternalUpdateRef.current = true;
        if (onScoreDataChange) onScoreDataChange(updatedScoreData);
      }
    }

    const updatedMap = {
      ...markingPositionsRef.current,
      [id]: updatedMark,
    };

    mirrorAliases(id, updatedMark, updatedMap);

    markingPositionsRef.current = updatedMap;
    setMarkingPositions(updatedMap);
    pushHistory(updatedMap);
    try {
      localStorage.setItem(storageKey, JSON.stringify(updatedMap));
    } catch (_) {}

    setSelectedMarkId(null);
    selectedMarkIdRef.current = null;
    const targetPage = currentMark.pageNum || currentPage;
    redrawPage(targetPage, null, null, null);
  }, [currentPage, redrawPage, mirrorAliases, storageKey, pushHistory, onScoreDataChange]);

  // Reassign an annotation / mark to a different question
  const handleReassignMarkQuestion = useCallback((markId, newQIdx) => {
    if (!markId || newQIdx < 0) return;
    const currentMark = markingPositionsRef.current[markId];
    if (!currentMark) return;

    const activeScore = currentScoreDataRef.current || scoreData;
    if (!activeScore?.result || !activeScore.result[newQIdx]) return;

    const oldQIdx = currentMark.questionIndex ?? currentMark.qIdx ?? -1;
    const newTargetQ = activeScore.result[newQIdx];
    const newQNum = newTargetQ.questionNumber || `${newQIdx + 1}`;
    const newQId = newTargetQ._id;

    let updatedResult = activeScore.result.map(q => ({
      ...q,
      annotations: Array.isArray(q.annotations) ? [...q.annotations] : [],
      stepEvaluations: Array.isArray(q.stepEvaluations) ? [...q.stepEvaluations] : [],
    }));

    const annId = currentMark.annotationId || (markId.startsWith("ann-") ? markId.replace(/^ann-(?:comment|loop|mark)-/, "") : null);
    const stepId = currentMark.stepId || (markId.startsWith("step-") ? markId.replace(/^step-mark-/, "") : null);

    let movedItem = null;
    let isStep = currentMark.markType === "step-mark" || currentMark.type === "tick" || currentMark.type === "cross";

    if (oldQIdx >= 0 && updatedResult[oldQIdx]) {
      const oldQ = updatedResult[oldQIdx];
      if (annId && Array.isArray(oldQ.annotations)) {
        const fIdx = oldQ.annotations.findIndex(a => String(a._id) === String(annId));
        if (fIdx >= 0) {
          movedItem = { ...oldQ.annotations[fIdx] };
          isStep = false;
          oldQ.annotations.splice(fIdx, 1);
        }
      }
      if (stepId && Array.isArray(oldQ.stepEvaluations)) {
        const fIdx = oldQ.stepEvaluations.findIndex(s => String(s._id) === String(stepId));
        if (fIdx >= 0) {
          movedItem = { ...oldQ.stepEvaluations[fIdx] };
          isStep = true;
          oldQ.stepEvaluations.splice(fIdx, 1);
        }
      }
    }

    const destQ = updatedResult[newQIdx];
    const cy = Math.round((currentMark.yRatio || 0.5) * 1000);
    const cx = Math.round((currentMark.xRatio || 0.5) * 1000);

    if (isStep) {
      const isTick = (currentMark.status || currentMark.type) === "correct" || currentMark.type === "tick";
      const newStep = movedItem || {
        _id: stepId || `step-custom-${Date.now()}`,
        stepText: isTick ? "Instructor checkmark" : "Instructor crossmark",
        status: isTick ? "correct" : "incorrect",
        pageNumber: currentMark.pageNum || currentPage,
        boundingBox: [Math.max(0, cy - 12), Math.max(0, cx - 20), Math.min(1000, cy + 12), Math.min(1000, cx + 20)],
        customX: currentMark.xRatio,
        customY: currentMark.yRatio,
        scale: currentMark.scale || 1.0,
        deleted: Boolean(currentMark.deleted),
        isCustom: true,
      };
      destQ.stepEvaluations.push(newStep);
    } else {
      const isComment = currentMark.type === "comment" || currentMark.markType === "comment" || markId.includes("comment");
      const isCircle = currentMark.type === "circle" || (!isComment && currentMark.type !== "line");
      const newAnn = movedItem || {
        _id: annId || `ann-custom-${Date.now()}`,
        annotationType: isComment ? "custom_note" : "specific_error",
        comment: currentMark.text || "",
        pageNumber: currentMark.pageNum || currentPage,
        boundingBox: [Math.max(0, cy - (isCircle ? 15 : 5)), Math.max(0, cx - (isCircle ? 25 : 30)), Math.min(1000, cy + (isCircle ? 15 : 5)), Math.min(1000, cx + (isCircle ? 25 : 30))],
        circleLine: isCircle,
        underline: !isComment && !isCircle,
        placement: "below",
        customX: !isComment ? currentMark.xRatio : undefined,
        customY: !isComment ? currentMark.yRatio : undefined,
        commentCustomX: isComment ? currentMark.xRatio : undefined,
        commentCustomY: isComment ? currentMark.yRatio : undefined,
        commentScale: currentMark.scale || 1.0,
        fontSize: currentMark.fontSize || 11,
        fontFamily: currentMark.fontFamily || "sans",
        deleted: Boolean(currentMark.deleted),
        deletedComment: isComment ? Boolean(currentMark.deleted) : false,
        isCustom: true,
      };
      destQ.annotations.push(newAnn);
    }

    const updatedScoreObj = {
      ...activeScore,
      result: updatedResult,
    };
    currentScoreDataRef.current = updatedScoreObj;
    setCurrentScoreData(updatedScoreObj);
    isInternalUpdateRef.current = true;
    if (onScoreDataChange) onScoreDataChange(updatedScoreObj);

    let updatedLabel = currentMark.label || "Annotation Mark";
    if (updatedLabel.includes("(Q")) {
      updatedLabel = updatedLabel.replace(/\(Q[^)]+\)/, `(Q${newQNum})`);
    } else {
      updatedLabel = `${updatedLabel} (Q${newQNum})`;
    }

    const updatedMark = {
      ...currentMark,
      questionIndex: newQIdx,
      questionId: newQId,
      questionNumber: newQNum,
      label: updatedLabel,
    };
    const updatedMap = {
      ...markingPositionsRef.current,
      [markId]: updatedMark,
    };
    markingPositionsRef.current = updatedMap;
    setMarkingPositions(updatedMap);
    pushHistory(updatedMap);

    const targetPage = currentMark.pageNum || currentPage;
    redrawPage(targetPage, null, null, markId);
  }, [currentPage, redrawPage, pushHistory, scoreData, onScoreDataChange]);

  // Mark duplication handler
  const handleDuplicateSelectedMark = useCallback(() => {
    const id = selectedMarkIdRef.current;
    if (!id) return;

    const currentMark = markingPositionsRef.current[id] || {};
    const newX = Math.min(0.95, (currentMark.xRatio || 0.5) + 0.03);
    const newY = Math.min(0.95, (currentMark.yRatio || 0.5) + 0.03);
    const targetQIdx = currentMark.questionIndex ?? (currentMark.qIdx ?? targetQuestionIndexRef.current);

    const activeScore = currentScoreDataRef.current || scoreData;
    const targetResult = activeScore?.result ? activeScore.result.map(q => ({
      ...q,
      annotations: Array.isArray(q.annotations) ? [...q.annotations] : [],
      stepEvaluations: Array.isArray(q.stepEvaluations) ? [...q.stepEvaluations] : [],
    })) : [];

    const targetQ = targetResult[targetQIdx];
    const targetQNum = targetQ?.questionNumber || `${targetQIdx + 1}`;
    const targetQId = targetQ?._id;

    const mType = currentMark.type || "comment";
    const cy = Math.round(newY * 1000);
    const cx = Math.round(newX * 1000);

    let newId = "";
    let duplicatedMark = null;

    if (mType === "comment") {
      const newAnnId = `ann-custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
      newId = `ann-comment-${newAnnId}`;
      const newAnn = {
        _id: newAnnId,
        annotationType: "custom_note",
        comment: currentMark.text || "Feedback comment",
        pageNumber: currentMark.pageNum || currentPage,
        boundingBox: [Math.max(0, cy - 15), Math.max(0, cx - 25), Math.min(1000, cy + 15), Math.min(1000, cx + 25)],
        circleLine: false,
        underline: false,
        placement: "below",
        commentCustomX: newX,
        commentCustomY: newY,
        commentScale: currentMark.scale || 1.0,
        fontSize: currentMark.fontSize || 11,
        fontFamily: currentMark.fontFamily || "sans",
        deleted: false,
        deletedComment: false,
        isCustom: true,
      };
      if (targetQ) targetQ.annotations.push(newAnn);
      duplicatedMark = {
        ...currentMark,
        id: newId,
        xRatio: newX,
        yRatio: newY,
        annotationId: newAnnId,
        questionIndex: targetQIdx,
        questionId: targetQId,
        questionNumber: targetQNum,
        isCustom: true,
        deleted: false,
      };
    } else if (mType === "tick" || mType === "cross") {
      const isTick = mType === "tick";
      const newStepId = `step-custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
      newId = `step-mark-${newStepId}`;
      const newStep = {
        _id: newStepId,
        stepText: isTick ? "Instructor checkmark" : "Instructor crossmark",
        status: isTick ? "correct" : "incorrect",
        pageNumber: currentMark.pageNum || currentPage,
        boundingBox: [Math.max(0, cy - 12), Math.max(0, cx - 20), Math.min(1000, cy + 12), Math.min(1000, cx + 20)],
        customX: newX,
        customY: newY,
        scale: currentMark.scale || 1.0,
        deleted: false,
        isCustom: true,
      };
      if (targetQ) targetQ.stepEvaluations.push(newStep);
      duplicatedMark = {
        ...currentMark,
        id: newId,
        xRatio: newX,
        yRatio: newY,
        stepId: newStepId,
        questionIndex: targetQIdx,
        questionId: targetQId,
        questionNumber: targetQNum,
        isCustom: true,
        deleted: false,
      };
    } else {
      const isCircle = mType === "circle";
      const newAnnId = `ann-custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
      newId = `ann-loop-${newAnnId}`;
      const newAnn = {
        _id: newAnnId,
        annotationType: "specific_error",
        comment: "",
        pageNumber: currentMark.pageNum || currentPage,
        boundingBox: [Math.max(0, cy - (isCircle ? 15 : 5)), Math.max(0, cx - (isCircle ? 25 : 30)), Math.min(1000, cy + (isCircle ? 15 : 5)), Math.min(1000, cx + (isCircle ? 25 : 30))],
        circleLine: isCircle,
        underline: !isCircle,
        placement: "below",
        customX: newX,
        customY: newY,
        scale: currentMark.scale || 1.0,
        strokeWidth: currentMark.strokeWidth || 2.0,
        deleted: false,
        deletedComment: false,
        isCustom: true,
      };
      if (targetQ) targetQ.annotations.push(newAnn);
      duplicatedMark = {
        ...currentMark,
        id: newId,
        xRatio: newX,
        yRatio: newY,
        annotationId: newAnnId,
        questionIndex: targetQIdx,
        questionId: targetQId,
        questionNumber: targetQNum,
        isCustom: true,
        deleted: false,
      };
    }

    if (targetQ && targetResult.length > 0) {
      const updatedScoreObj = { ...activeScore, result: targetResult };
      currentScoreDataRef.current = updatedScoreObj;
      setCurrentScoreData(updatedScoreObj);
      isInternalUpdateRef.current = true;
      if (onScoreDataChange) onScoreDataChange(updatedScoreObj);
    }

    const updatedMap = {
      ...markingPositionsRef.current,
      [newId]: duplicatedMark,
    };

    markingPositionsRef.current = updatedMap;
    setMarkingPositions(updatedMap);
    pushHistory(updatedMap);
    try {
      localStorage.setItem(storageKey, JSON.stringify(updatedMap));
    } catch (_) {}

    setSelectedMarkId(newId);
    selectedMarkIdRef.current = newId;
    const targetPage = duplicatedMark.pageNum || currentPage;
    redrawPage(targetPage, null, null, newId);
  }, [currentPage, redrawPage, storageKey, pushHistory, scoreData, onScoreDataChange]);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
        return;
      }

      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedMarkIdRef.current) {
          e.preventDefault();
          handleDeleteSelectedMark();
        }
      } else if (e.key === "Escape") {
        if (activeToolRef.current) {
          setActiveTool(null);
          activeToolRef.current = null;
        } else if (selectedMarkIdRef.current) {
          setSelectedMarkId(null);
          selectedMarkIdRef.current = null;
          redrawAllPages();
        }
      } else if (e.key === "z" && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (
        (e.key === "y" && (e.ctrlKey || e.metaKey)) ||
        (e.key === "z" && (e.ctrlKey || e.metaKey) && e.shiftKey)
      ) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleDeleteSelectedMark, handleUndo, handleRedo, redrawAllPages]);

  // Active Selected Mark Descriptor for Inspector
  const selectedMark = useMemo(() => {
    if (!selectedMarkId) return null;
    const pos = markingPositions[selectedMarkId] || {};
    let descriptor = null;
    const markingsOnCurrent = markingsByPageRef.current[currentPage] || [];
    descriptor = markingsOnCurrent.find((m) => m.id === selectedMarkId);
    if (!descriptor) {
      for (const p of Object.keys(markingsByPageRef.current)) {
        const found = (markingsByPageRef.current[p] || []).find((m) => m.id === selectedMarkId);
        if (found) {
          descriptor = found;
          break;
        }
      }
    }

    return {
      id: selectedMarkId,
      type: pos.type || descriptor?.type || "mark",
      label: descriptor?.label || pos.label || "Annotation Mark",
      pageNum: pos.pageNum || descriptor?.pageNum || currentPage,
      qNum: descriptor?.qNum || pos.qNum || pos.questionNumber,
      qKey: descriptor?.qKey || pos.qKey,
      qIdx: pos.questionIndex ?? (descriptor?.qIdx ?? pos.qIdx),
      questionIndex: pos.questionIndex ?? (descriptor?.qIdx ?? pos.qIdx),
      questionNumber: pos.questionNumber || descriptor?.qNum || pos.qNum,
      questionId: descriptor?.questionId || pos.questionId,
      status: pos.status || descriptor?.status,
      annotationId: pos.annotationId || descriptor?.annotationId,
      stepId: pos.stepId || descriptor?.stepId,
      score: pos.score !== undefined ? pos.score : (descriptor?.score !== undefined ? descriptor.score : undefined),
      maxScore: descriptor?.maxScore !== undefined ? descriptor.maxScore : (pos.maxScore !== undefined ? pos.maxScore : undefined),
      xRatio:
        pos.xRatio != null
          ? pos.xRatio
          : descriptor?.x != null && canvasRefs.current[currentPage]
          ? +(descriptor.x / canvasRefs.current[currentPage].width).toFixed(4)
          : 0.5,
      yRatio:
        pos.yRatio != null
          ? pos.yRatio
          : descriptor?.y != null && canvasRefs.current[currentPage]
          ? +(descriptor.y / canvasRefs.current[currentPage].height).toFixed(4)
          : 0.5,
      scale: pos.scale != null ? pos.scale : descriptor?.scale != null ? descriptor.scale : 1.0,
      color: pos.color || descriptor?.color || "#c53030",
      strokeWidth: pos.strokeWidth || descriptor?.strokeWidth || 2.0,
      text: pos.text !== undefined ? pos.text : descriptor?.text !== undefined ? descriptor.text : undefined,
      fontSize: pos.fontSize || descriptor?.fontSize || 11,
      fontFamily: pos.fontFamily || descriptor?.fontFamily || "sans",
      isCustom: Boolean(pos.isCustom || descriptor?.isCustom || selectedMarkId.startsWith("custom-") || selectedMarkId.includes("-custom-")),
    };
  }, [selectedMarkId, markingPositions, currentPage]);

  // Save custom positions to backend database and localStorage
  const handleSavePositions = async () => {
    setIsSaving(true);
    try {
      const activeScoreData = currentScoreDataRef.current || currentScoreData || scoreData;

      // Reconcile all custom markings from markingPositionsRef.current into activeScoreData.result
      if (activeScoreData?.result && Array.isArray(activeScoreData.result)) {
        const updatedResult = activeScoreData.result.map((q) => ({
          ...q,
          annotations: Array.isArray(q.annotations) ? [...q.annotations] : [],
          stepEvaluations: Array.isArray(q.stepEvaluations) ? [...q.stepEvaluations] : [],
        }));

        Object.entries(markingPositionsRef.current || {}).forEach(([mId, mark]) => {
          if (!mark || typeof mark !== "object") return;

          // Skip any positional alias keys - only process canonical IDs!
          if (/^(?:ann-comment|ann-loop|ann-mark|step-mark)-(?:q)?\d+-(?:[as])?\d+$/.test(mId)) {
            return;
          }

          let targetQIdx = mark.questionIndex;
          if (targetQIdx === undefined || targetQIdx < 0 || targetQIdx >= updatedResult.length) {
            if (mark.questionId) {
              targetQIdx = updatedResult.findIndex((q) => String(q._id) === String(mark.questionId));
            }
          }
          if (targetQIdx === undefined || targetQIdx < 0 || targetQIdx >= updatedResult.length) {
            const annId = mark.annotationId || (mId.startsWith("ann-") ? mId.replace(/^ann-(?:loop|comment|mark)-/, "") : null);
            const stepId = mark.stepId || (mId.startsWith("step-mark-") ? mId.replace("step-mark-", "") : null);
            for (let qi = 0; qi < updatedResult.length; qi++) {
              if (annId && updatedResult[qi].annotations?.some(a => String(a._id) === String(annId) || String(a.id) === String(annId))) {
                targetQIdx = qi;
                break;
              }
              if (stepId && updatedResult[qi].stepEvaluations?.some(s => String(s._id) === String(stepId) || String(s.id) === String(stepId))) {
                targetQIdx = qi;
                break;
              }
            }
          }
          if (targetQIdx === undefined || targetQIdx < 0 || targetQIdx >= updatedResult.length) {
            if (mId.startsWith("q-score-")) {
              const cleanQ = mId.replace("q-score-", "").replace(/^q/, "").trim();
              const foundIdx = updatedResult.findIndex(q => String(q.questionNumber).trim() === cleanQ || String(q.questionNumber).trim() === `q${cleanQ}`);
              if (foundIdx !== -1) targetQIdx = foundIdx;
            }
          }
          if (targetQIdx === undefined || targetQIdx < 0 || targetQIdx >= updatedResult.length) {
            targetQIdx = 0;
          }

          const targetQ = updatedResult[targetQIdx];
          if (!targetQ) return;

          let mType = mark.type || mark.markType || "";
          if (!mType) {
            if (mId.startsWith("q-score-")) mType = "question-score";
            else if (mId.startsWith("ann-comment-")) mType = "comment";
            else if (mId.startsWith("ann-loop-") || mId.startsWith("ann-mark-")) mType = "ann-loop";
            else if (mId.startsWith("step-mark-")) mType = "step-mark";
            else if (mId === "top-score-stamp") mType = "top-score";
          }
          const isDeleted = Boolean(mark.deleted);

          // 1. Question score circle
          if (mType === "question-score" || mId.startsWith("q-score")) {
            targetQ.scorePosition = {
              ...(targetQ.scorePosition || {}),
              pageNumber: mark.pageNum || targetQ.scorePosition?.pageNumber || currentPage,
              customX: mark.xRatio,
              customY: mark.yRatio,
              scale: (typeof mark.scale === "number" && !isNaN(mark.scale) && mark.scale > 0) ? mark.scale : 1.0,
              deleted: isDeleted,
            };
            return;
          }

          const isCustom = Boolean(mark.isCustom || mId.startsWith("custom-") || mId.includes("-custom-"));

          if (mType === "comment" || mId.startsWith("ann-comment") || mId.includes("comment")) {
            const annId = mark.annotationId || (mId.startsWith("ann-comment-") ? mId.replace("ann-comment-", "") : mId);
            // Match by unique _id first, then fallback to index
            let existingIdx = targetQ.annotations.findIndex(
              (a) => a._id && String(a._id) === String(annId)
            );
            if (existingIdx === -1 && mark.annotationIndex != null && targetQ.annotations[mark.annotationIndex]) {
              existingIdx = mark.annotationIndex;
            }

            if (existingIdx >= 0) {
              targetQ.annotations[existingIdx] = {
                ...targetQ.annotations[existingIdx],
                commentCustomX: mark.xRatio,
                commentCustomY: mark.yRatio,
                commentScale: (typeof mark.scale === "number" && !isNaN(mark.scale) && mark.scale > 0) ? mark.scale : 1.0,
                scale: (typeof mark.scale === "number" && !isNaN(mark.scale) && mark.scale > 0) ? mark.scale : 1.0,
                fontSize: mark.fontSize || targetQ.annotations[existingIdx].fontSize || 11,
                fontFamily: mark.fontFamily || targetQ.annotations[existingIdx].fontFamily || "sans",
                deletedComment: isDeleted,
                ...(mark.text != null ? { comment: mark.text } : {}),
              };
            } else if (isCustom && !isDeleted) {
              const annObj = {
                _id: annId,
                annotationType: "custom_note",
                comment: mark.text != null ? mark.text : "Feedback comment",
                pageNumber: mark.pageNum || currentPage,
                boundingBox: [
                  Math.max(0, Math.round((mark.yRatio || 0.5) * 1000) - 15),
                  Math.max(0, Math.round((mark.xRatio || 0.5) * 1000) - 25),
                  Math.min(1000, Math.round((mark.yRatio || 0.5) * 1000) + 15),
                  Math.min(1000, Math.round((mark.xRatio || 0.5) * 1000) + 25),
                ],
                circleLine: false,
                underline: false,
                placement: "below",
                commentCustomX: mark.xRatio,
                commentCustomY: mark.yRatio,
                commentScale: (typeof mark.scale === "number" && !isNaN(mark.scale) && mark.scale > 0) ? mark.scale : 1.0,
                scale: (typeof mark.scale === "number" && !isNaN(mark.scale) && mark.scale > 0) ? mark.scale : 1.0,
                fontSize: mark.fontSize || 11,
                fontFamily: mark.fontFamily || "sans",
                deleted: isDeleted,
                deletedComment: isDeleted,
                isCustom: true,
              };
              targetQ.annotations.push(annObj);
            }
          } else if (mType === "tick" || mType === "cross" || mType === "step-mark") {
            const stepId = mark.stepId || (mId.startsWith("step-mark-") ? mId.replace("step-mark-", "") : mId);
            const isTick = mType === "tick" || mark.status === "correct";
            let existingIdx = targetQ.stepEvaluations.findIndex(
              (s) => s._id && String(s._id) === String(stepId)
            );
            if (existingIdx === -1 && mark.stepIndex != null && targetQ.stepEvaluations[mark.stepIndex]) {
              existingIdx = mark.stepIndex;
            }

            if (existingIdx >= 0) {
              targetQ.stepEvaluations[existingIdx] = {
                ...targetQ.stepEvaluations[existingIdx],
                customX: mark.xRatio,
                customY: mark.yRatio,
                scale: (typeof mark.scale === "number" && !isNaN(mark.scale) && mark.scale > 0) ? mark.scale : 1.0,
                deleted: isDeleted,
              };
            } else if (isCustom && !isDeleted) {
              const stepObj = {
                _id: stepId,
                status: isTick ? "correct" : "incorrect",
                stepText: isTick ? "Instructor checkmark" : "Instructor crossmark",
                pageNumber: mark.pageNum || currentPage,
                boundingBox: [
                  Math.max(0, Math.round((mark.yRatio || 0.5) * 1000) - 15),
                  Math.max(0, Math.round((mark.xRatio || 0.5) * 1000) - 15),
                  Math.min(1000, Math.round((mark.yRatio || 0.5) * 1000) + 15),
                  Math.min(1000, Math.round((mark.xRatio || 0.5) * 1000) + 15),
                ],
                customX: mark.xRatio,
                customY: mark.yRatio,
                scale: (typeof mark.scale === "number" && !isNaN(mark.scale) && mark.scale > 0) ? mark.scale : 1.0,
                strokeWidth: mark.strokeWidth || 2.0,
                deleted: isDeleted,
                isCustom: true,
              };
              targetQ.stepEvaluations.push(stepObj);
            }
          } else if (mType === "circle" || mType === "line" || mType === "ann-loop") {
            const annId = mark.annotationId || (mId.startsWith("ann-loop-") ? mId.replace("ann-loop-", "") : (mId.startsWith("ann-mark-") ? mId.replace("ann-mark-", "") : mId));
            const isCircle = mType === "circle" || mType === "ann-loop";
            let existingIdx = targetQ.annotations.findIndex(
              (a) => a._id && String(a._id) === String(annId)
            );
            if (existingIdx === -1 && mark.annotationIndex != null && targetQ.annotations[mark.annotationIndex]) {
              existingIdx = mark.annotationIndex;
            }

            if (existingIdx >= 0) {
              targetQ.annotations[existingIdx] = {
                ...targetQ.annotations[existingIdx],
                customX: mark.xRatio,
                customY: mark.yRatio,
                scale: (typeof mark.scale === "number" && !isNaN(mark.scale) && mark.scale > 0) ? mark.scale : 1.0,
                radiusRatio: mark.radiusRatio,
                heightRatio: mark.heightRatio,
                deleted: isDeleted,
              };
            } else if (isCustom && !isDeleted) {
              const annObj = {
                _id: annId,
                annotationType: isCircle ? "circle" : "underline",
                circleLine: isCircle,
                underline: !isCircle,
                pageNumber: mark.pageNum || currentPage,
                boundingBox: [
                  Math.max(0, Math.round((mark.yRatio || 0.5) * 1000) - 20),
                  Math.max(0, Math.round((mark.xRatio || 0.5) * 1000) - 20),
                  Math.min(1000, Math.round((mark.yRatio || 0.5) * 1000) + 20),
                  Math.min(1000, Math.round((mark.xRatio || 0.5) * 1000) + 20),
                ],
                placement: "below",
                customX: mark.xRatio,
                customY: mark.yRatio,
                scale: (typeof mark.scale === "number" && !isNaN(mark.scale) && mark.scale > 0) ? mark.scale : 1.0,
                radiusRatio: mark.radiusRatio,
                heightRatio: mark.heightRatio,
                strokeWidth: mark.strokeWidth || 2.0,
                deleted: isDeleted,
                deletedComment: isDeleted,
                isCustom: true,
              };
              targetQ.annotations.push(annObj);
            }
          }
        });

        // Deduplicate annotations and stepEvaluations to clean any previous duplicates
        updatedResult.forEach((q) => {
          const seenAnnIds = new Set();
          const cleanAnns = [];
          (q.annotations || []).forEach((a) => {
            const aId = String(a._id || "");
            if (a.isCustom && /^(?:q)?\d+-(?:a)?\d+$/.test(aId)) return;
            if (aId && seenAnnIds.has(aId)) return;
            if (aId) seenAnnIds.add(aId);
            cleanAnns.push(a);
          });
          q.annotations = cleanAnns;

          const seenStepIds = new Set();
          const cleanSteps = [];
          (q.stepEvaluations || []).forEach((s) => {
            const sId = String(s._id || "");
            if (s.isCustom && /^(?:q)?\d+-(?:s)?\d+$/.test(sId)) return;
            if (sId && seenStepIds.has(sId)) return;
            if (sId) seenStepIds.add(sId);
            cleanSteps.push(s);
          });
          q.stepEvaluations = cleanSteps;
        });

        activeScoreData.result = updatedResult;
      }

      const positionsToSave = expandPositionAliases(markingPositionsRef.current, activeScoreData);

      // Clean positionsToSave of any alias pollution for custom marks
      Object.keys(positionsToSave).forEach((k) => {
        if (/^(?:ann-comment|ann-loop|ann-mark|step-mark)-(?:q)?\d+-(?:[as])?\d+$/.test(k)) {
          if (positionsToSave[k]?.isCustom || positionsToSave[k]?.annotationId?.includes("custom-") || positionsToSave[k]?.stepId?.includes("custom-")) {
            delete positionsToSave[k];
          }
        }
      });

      // 1. Save to localStorage immediately
      try {
        localStorage.setItem(storageKey, JSON.stringify(positionsToSave));
      } catch (_) {}

      // 2. Persist to Backend server using modern unified save-annotations API
      if (serverUrl && answerPaperId && fetchWithAuth) {
        // Reconcile topScorePosition from markingPositionsRef.current
        const topPos = markingPositionsRef.current?.["top-score-stamp"];
        if (topPos) {
          activeScoreData.topScorePosition = {
            ...(activeScoreData.topScorePosition || {}),
            pageNumber: 1,
            customX: topPos.xRatio,
            customY: topPos.yRatio,
            scale: (typeof topPos.scale === "number" && !isNaN(topPos.scale) && topPos.scale > 0) ? topPos.scale : 1.0,
            deleted: Boolean(topPos.deleted),
          };
        }

        const payload = {
          result: activeScoreData.result,
          topScorePosition: activeScoreData.topScorePosition,
          totalScore: activeScoreData.totalScore,
          annotationPositions: positionsToSave,
        };

        const response = await fetchWithAuth(
          `${serverUrl}/api/score/save-annotations/${answerPaperId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
            credentials: "include",
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || "Failed to save annotations on server");
        }

        const resData = await response.json();
        if (resData.score) {
          const cleanResScore = sanitizeScoreData(resData.score);
          currentScoreDataRef.current = cleanResScore;
          setCurrentScoreData(cleanResScore);
          isInternalUpdateRef.current = true;
          if (onScoreDataChange) onScoreDataChange(cleanResScore);
        }

        // Clear modified scores tracking after saving
        modifiedQuestionScoresRef.current = {};
      }

      markingPositionsRef.current = positionsToSave;
      savedStateSnapshotRef.current = serializeStudioState(positionsToSave, {});
      setMarkingPositions({ ...positionsToSave });
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3500);
      swalToast("Scores and annotation positions saved successfully!");
    } catch (err) {
      console.error("Failed to save annotation positions:", err);
      swalToast(err.message || "Failed to save annotation positions on server", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Actions
  const handleZoomIn = () => setZoom((z) => Math.min(2.5, +(z + 0.15).toFixed(2)));
  const handleZoomOut = () => setZoom((z) => Math.max(0.5, +(z - 0.15).toFixed(2)));
  const handleResetZoom = () => setZoom(1.0);
  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  const handleFitToWidth = () => {
    const bodyEl = documentBodyRef.current;
    if (bodyEl) {
      const availWidth = bodyEl.clientWidth - 48;
      const isSwapped = rotation === 90 || rotation === 270;
      const targetW = isSwapped ? 1180 : 860;
      if (availWidth > 200) {
        const fitZoom = +(availWidth / targetW).toFixed(2);
        if (Math.abs(zoom - fitZoom) < 0.05) {
          setZoom(1.0);
        } else {
          setZoom(Math.max(0.4, Math.min(2.5, fitZoom)));
        }
        return;
      }
    }
    setZoom(1.0);
  };

  const handleGoToPage = (pageNum) => {
    const clamped = Math.max(1, Math.min(totalPages, pageNum));
    setCurrentPage(clamped);
  };

  const handlePrevPage = () => handleGoToPage(currentPage - 1);
  const handleNextPage = () => handleGoToPage(currentPage + 1);

  const hasChanges = useMemo(() => {
    const currentSnapshot = serializeStudioState(
      markingPositions,
      modifiedQuestionScoresRef.current
    );
    return currentSnapshot !== savedStateSnapshotRef.current;
  }, [markingPositions]);

  // Compute total obtained and max score
  const activeScoreData = currentScoreData || scoreData;
  const totalObtained =
    activeScoreData?.result?.reduce((sum, q) => sum + (Number(q.obtainedScore) || 0), 0) ?? 0;
  const totalMax =
    activeScoreData?.result?.reduce((sum, q) => sum + (Number(q.maxScore) || 0), 0) ?? 0;

  // When currentPage changes, automatically align targetQuestionIndex to the first question on the new page
  useEffect(() => {
    if (!activeScoreData?.result || !Array.isArray(activeScoreData.result)) return;
    const questions = activeScoreData.result;
    const currentQ = questions[targetQuestionIndexRef.current];
    const isCurrentOnPage =
      currentQ &&
      (currentQ.scorePosition?.pageNumber === currentPage ||
       (Array.isArray(currentQ.annotations) && currentQ.annotations.some((a) => a?.pageNumber === currentPage)) ||
       (Array.isArray(currentQ.stepEvaluations) && currentQ.stepEvaluations.some((s) => s?.pageNumber === currentPage)) ||
       currentQ.pageNumber === currentPage);

    if (!isCurrentOnPage) {
      const firstQOnPageIdx = questions.findIndex(
        (q) =>
          q.scorePosition?.pageNumber === currentPage ||
          (Array.isArray(q.annotations) && q.annotations.some((a) => a?.pageNumber === currentPage)) ||
          (Array.isArray(q.stepEvaluations) && q.stepEvaluations.some((s) => s?.pageNumber === currentPage)) ||
          q.pageNumber === currentPage
      );
      if (firstQOnPageIdx >= 0) {
        setTargetQuestionIndex(firstQOnPageIdx);
        targetQuestionIndexRef.current = firstQOnPageIdx;
      }
    }
  }, [currentPage, activeScoreData?.result]);

  const handleBackToResults = useCallback(async () => {
    if (hasChanges && !isSaved) {
      const ok = await swalConfirm(
        "You have unsaved changes to annotations. Do you want to return to results table without saving?",
        "Yes, Return",
        "Keep Editing"
      );
      if (!ok) return;
    }
    if (onClose) {
      onClose();
    }
  }, [hasChanges, isSaved, onClose]);

  const paperFileName =
    paperUrl?.split("/")?.pop()?.split("?")[0] || "Merged_Scans.pdf";

  return (
    <div className="annotated-paper-viewer-container" ref={containerRef}>
      {/* Unified Studio Top Global Header Bar */}
      <div className="studio-header-bar">
        {/* Left Section: Back to Results + Student Info */}
        <div className="studio-header-left">
          <button
            type="button"
            className="studio-header-back-btn"
            onClick={handleBackToResults}
            title="Return to Results Table"
          >
            <i className="fa-solid fa-arrow-left" />
            <span>Back to Results</span>
          </button>

          <div className="studio-header-divider" />

          <div className="studio-student-chip">
            <span className="studio-student-name">
              {studentInfo?.name ? `${studentInfo.name}'s Paper` : paperFileName}
            </span>
            {studentInfo?.referenceId && (
              <span className="studio-roll-badge">
                Roll: #{studentInfo.referenceId}
              </span>
            )}
            {totalMax > 0 && (
              <span className="studio-score-badge">
                Score: <strong>{totalObtained} / {totalMax}</strong>
              </span>
            )}
          </div>
        </div>

        {/* Center Section: Primary "+ Add Mark" Studio Button + Essential Canvas Controls */}
        <div className="studio-header-center">
          <button
            type="button"
            className="studio-header-add-btn"
            onClick={() => setShowAddModal(true)}
            title="Add a new mark, tick, cross, comment, or score"
          >
            <i className="fa-solid fa-plus" />
            <span>Add Mark</span>
          </button>

          <div className="studio-header-divider" />

          {/* Undo / Redo */}
          <div className="studio-history-group">
            <button
              type="button"
              className="studio-icon-btn"
              disabled={historyIndex <= 0}
              onClick={handleUndo}
              title="Undo (Ctrl+Z)"
            >
              <i className="fa-solid fa-arrow-rotate-left" />
            </button>
            <button
              type="button"
              className="studio-icon-btn"
              disabled={historyIndex >= history.length - 1}
              onClick={handleRedo}
              title="Redo (Ctrl+Y)"
            >
              <i className="fa-solid fa-arrow-rotate-right" />
            </button>
          </div>

          <div className="studio-header-divider" />

          {/* Page Navigation */}
          <div className="studio-page-nav-group">
            <button
              type="button"
              className="studio-icon-btn"
              disabled={currentPage <= 1}
              onClick={handlePrevPage}
              title="Previous Page"
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
            <span className="studio-page-text">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="studio-icon-btn"
              disabled={currentPage >= totalPages}
              onClick={handleNextPage}
              title="Next Page"
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
          </div>

          <div className="studio-header-divider" />

          {/* Zoom Stepper */}
          <div className="studio-zoom-group">
            <button
              type="button"
              className="studio-icon-btn"
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
              title="Zoom Out"
            >
              <i className="fa-solid fa-minus" />
            </button>
            <button
              type="button"
              className="studio-zoom-text-btn"
              onClick={handleResetZoom}
              title="Reset Zoom to 100%"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              className="studio-icon-btn"
              onClick={handleZoomIn}
              disabled={zoom >= 2.5}
              title="Zoom In"
            >
              <i className="fa-solid fa-plus" />
            </button>
            <button
              type="button"
              className="studio-fit-btn"
              onClick={handleFitToWidth}
              title="Fit to Width"
            >
              Fit
            </button>
          </div>

          {/* Rotate Button */}
          <button
            type="button"
            className="studio-icon-btn rotate-btn"
            onClick={handleRotate}
            title="Rotate Page (90° Clockwise)"
          >
            <i className="fa-solid fa-rotate-right" />
          </button>

        </div>

        {/* Right Section: Save Changes */}
        <div className="studio-header-right">
          <button
            type="button"
            className={`studio-save-btn ${isSaved ? "saved" : hasChanges ? "dirty" : "clean"}`}
            onClick={hasChanges && !isSaving ? handleSavePositions : undefined}
            disabled={!hasChanges || isSaving}
            title={
              isSaving
                ? "Saving changes..."
                : isSaved
                ? "All changes saved"
                : hasChanges
                ? "Save changes to database"
                : "No changes to save"
            }
          >
            {isSaving ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" />
                <span>Saving...</span>
              </>
            ) : isSaved ? (
              <>
                <i className="fa-solid fa-check" />
                <span>Saved</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk" />
                <span>Save</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Workspace: Full-Width PDF Canvas Viewer */}
      <div className="annotation-studio-workspace">
        {/* PDF Document Workspace */}
        <div className="annotation-canvas-workspace">
          {/* Document Canvas Body Area */}
          <div className="annotated-document-body" ref={documentBodyRef}>
            {loading ? (
              <div className="annotated-loading-state">
                <i className="fa-solid fa-circle-notch fa-spin annotated-spinner" />
                <h3>{loadingText}</h3>
                <p>Rendering student answer paper...</p>
              </div>
            ) : error ? (
              <div className="annotated-error-state">
                <i className="fa-solid fa-triangle-exclamation" />
                <h3>Could Not Load Paper</h3>
                <p>{error}</p>
                {paperUrl && (
                  <a
                    href={paperUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="annotated-fallback-link"
                  >
                    <i className="fa-solid fa-eye" /> View Raw Paper URL
                  </a>
                )}
              </div>
            ) : (
              <div
                className="annotated-pages-stream"
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: "top center",
                  transition: "transform 0.15s ease",
                }}
              >
                <div
                  key={currentPage}
                  className="annotated-page-card"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    margin: (rotation === 90 || rotation === 270) ? "40px auto" : "0 auto",
                  }}
                >
                  <div className="annotated-page-badge-corner">Page {currentPage}</div>
                  <canvas
                    ref={(el) => {
                      if (el) canvasRefs.current[currentPage] = el;
                    }}
                    className={`annotated-canvas-page ${activeTool ? "placing-mark" : ""}`}
                    onPointerDown={(e) => handlePointerDown(e, currentPage)}
                    onPointerMove={(e) => handlePointerMove(e, currentPage)}
                    onPointerUp={(e) => handlePointerUp(e, currentPage)}
                    onPointerCancel={(e) => handlePointerUp(e, currentPage)}
                    onPointerLeave={(e) => handlePointerLeave(e, currentPage)}
                  />
                  <StudioFloatingToolbar
                    selectedMark={selectedMark}
                    canvas={canvasRefs.current[currentPage]}
                    pageNum={currentPage}
                    onUpdate={handleUpdateSelectedMark}
                    onDelete={handleDeleteSelectedMark}
                    onClose={() => {
                      setSelectedMarkId(null);
                      selectedMarkIdRef.current = null;
                      redrawAllPages();
                    }}
                    questions={activeScoreData?.result || []}
                    onReassignQuestion={handleReassignMarkQuestion}
                    onDuplicate={handleDuplicateSelectedMark}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Studio Visual "+ Add Mark" Modal */}
      <StudioAddModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddMark={handleAddMarkDirectly}
        currentPage={currentPage}
        questions={activeScoreData?.result || []}
        initialTargetQuestionIndex={targetQuestionIndex}
      />
    </div>
  );
};

export default AnnotationStudio;
